import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs, doc, updateDoc, addDoc, getDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  multiFactor,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from "firebase/auth";
import { ref, getDownloadURL } from "firebase/storage";
import { db, auth, storage } from "./firebase";
import "./App.css";
import AnalyticsPage from "./pages/AnalyticsPage";
import SellerAnalyticsPage from "./pages/SellerAnalyticsPage";
import BuyerRetentionPage from "./pages/BuyerRetentionPage";
import ConversionMetricsPage from "./pages/ConversionMetricsPage";
import FraudRiskPage from "./pages/FraudRiskPage";
import SuspiciousBiddingPage from "./pages/SuspiciousBiddingPage";
import DeviceMonitoringPage from "./pages/DeviceMonitoringPage";
import AuctionPerformancePage from "./pages/AuctionPerformancePage";
import NotificationCenterPage from "./pages/NotificationCenterPage";
import CategoryManagementPage from "./pages/CategoryManagementPage";
function App() {
  const ADMIN_ENTRY_HASH = "#/sebanya@31";

const [isAdminEntry, setIsAdminEntry] = useState(
  () =>
    window.location.hash ===
    ADMIN_ENTRY_HASH
);

useEffect(() => {
  const handleAdminEntryHash = () => {
    setIsAdminEntry(
      window.location.hash ===
        ADMIN_ENTRY_HASH
    );
  };

  handleAdminEntryHash();

  window.addEventListener(
    "hashchange",
    handleAdminEntryHash
  );

  return () => {
    window.removeEventListener(
      "hashchange",
      handleAdminEntryHash
    );
  };
}, []);
  const [users, setUsers] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const [userSearch, setUserSearch] = useState("");
  const [userPhoneFilter, setUserPhoneFilter] = useState("all");
  const [userSortMode, setUserSortMode] = useState("default");
  const [auctionStatusFilter, setAuctionStatusFilter] = useState("all");
  const [auctionSearch, setAuctionSearch] = useState("");
  const [transactionStatusFilter, setTransactionStatusFilter] = useState("all");
  const [adminLogSearch, setAdminLogSearch] = useState("");
  const [buyerSearch, setBuyerSearch] = useState("");
  const [adminLogActionFilter, setAdminLogActionFilter] = useState("all");
  const [adminUser, setAdminUser] = useState(null);
const [authLoading, setAuthLoading] = useState(true);
const [loginEmail, setLoginEmail] = useState("");
const [loginPassword, setLoginPassword] = useState("");
const [loginError, setLoginError] = useState("");

const [mfaResolver, setMfaResolver] = useState(null);
const [mfaLoginCode, setMfaLoginCode] = useState("");
const [mfaLoginVerificationId, setMfaLoginVerificationId] = useState("");

const [mfaEnrollmentRequired, setMfaEnrollmentRequired] = useState(false);
const [mfaPhoneNumber, setMfaPhoneNumber] = useState("");
const [mfaEnrollmentCode, setMfaEnrollmentCode] = useState("");
const [mfaEnrollmentVerificationId, setMfaEnrollmentVerificationId] =
  useState("");
const [mfaEnrollmentCodeSent, setMfaEnrollmentCodeSent] =
  useState(false);
const [mfaEnrollmentError, setMfaEnrollmentError] = useState("");
const [mfaEnrollmentLoading, setMfaEnrollmentLoading] =
  useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [selectedUserRatings, setSelectedUserRatings] = useState([]);
  const [selectedUserRatingsLoading, setSelectedUserRatingsLoading] =
    useState(false);
  const [selectedAuctionDetails, setSelectedAuctionDetails] = useState(null);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [auctionRecordingDetails, setAuctionRecordingDetails] = useState(null);
  const [auctionRecordingSessions, setAuctionRecordingSessions] = useState([]);
  const [activeSection, setActiveSection] = useState("dashboard");
  const [globalDateRange, setGlobalDateRange] = useState("all");


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (!firebaseUser) {
          setAdminUser(null);
  
          setMfaEnrollmentRequired(false);
          setMfaPhoneNumber("");
          setMfaEnrollmentCode("");
          setMfaEnrollmentVerificationId("");
          setMfaEnrollmentCodeSent(false);
          setMfaEnrollmentError("");
  
          setAuthLoading(false);
          return;
        }
  
        const userRef = doc(db, "users", firebaseUser.uid);
        const userSnap = await getDoc(userRef);
  
        if (!userSnap.exists() || userSnap.data()?.isOwner !== true) {
          await signOut(auth);
  
          setAdminUser(null);
          setMfaEnrollmentRequired(false);
  
          setLoginError(
            "Access denied. This account is not authorized as owner."
          );
  
          setAuthLoading(false);
          return;
        }
  
        const enrolledFactors =
          multiFactor(firebaseUser).enrolledFactors || [];
  
        const hasSmsMfa =
          enrolledFactors.some(
            (factor) =>
              factor.factorId ===
              PhoneMultiFactorGenerator.FACTOR_ID
          );
  
        if (hasSmsMfa) {
          setMfaEnrollmentRequired(false);
  
          setMfaPhoneNumber("");
          setMfaEnrollmentCode("");
          setMfaEnrollmentVerificationId("");
          setMfaEnrollmentCodeSent(false);
          setMfaEnrollmentError("");
  
          setAdminUser(firebaseUser);
          setAuthLoading(false);
          return;
        }
  
        /*
         * Firebase requires a verified email before
         * enrolling a second authentication factor.
         */
        if (firebaseUser.emailVerified !== true) {
          await signOut(auth);
  
          setAdminUser(null);
          setMfaEnrollmentRequired(false);
  
          setLoginError(
            "Your web-admin email must be verified before SMS two-factor authentication can be enabled."
          );
  
          setAuthLoading(false);
          return;
        }
  
        /*
         * This is an authorized owner account,
         * but it does not have an SMS MFA factor yet.
         *
         * Do NOT open the admin dashboard.
         * Show the SMS enrollment screen instead.
         */
        setMfaEnrollmentRequired(true);
  
        setMfaPhoneNumber("");
        setMfaEnrollmentCode("");
        setMfaEnrollmentVerificationId("");
        setMfaEnrollmentCodeSent(false);
        setMfaEnrollmentError("");
  
        setAdminUser(null);
        setAuthLoading(false);
      } catch (error) {
        console.error(
          "Admin auth check error:",
          error
        );
  
        setAdminUser(null);
        setMfaEnrollmentRequired(false);
  
        setLoginError(
          "Could not verify administrator security settings."
        );
  
        setAuthLoading(false);
      }
    });
  
    return () => unsubscribe();
  }, []);
  useEffect(() => {
    if (!adminUser) return;

    const loadDashboardData = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const usersList = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const auctionsSnapshot = await getDocs(collection(db, "auctions"));
        const auctionsList = await Promise.all(
          auctionsSnapshot.docs.map(async (auctionDoc) => {
            const bidHistorySnapshot = await getDocs(
              collection(db, "auctions", auctionDoc.id, "bidHistory")
            );
        
            const bidHistory = bidHistorySnapshot.docs
              .map((bidDoc) => ({
                id: bidDoc.id,
                ...bidDoc.data(),
              }))
              .sort((a, b) => Number(a.createdAtMs || 0) - Number(b.createdAtMs || 0));
        
            return {
              id: auctionDoc.id,
              ...auctionDoc.data(),
              bidHistory,
            };
          })
        );

        const adminLogsSnapshot = await getDocs(
          collection(db, "adminLogs")
        );

        const adminLogsList =
          adminLogsSnapshot.docs.map(
            (adminLogDoc) => ({
              id: adminLogDoc.id,
              ...adminLogDoc.data(),
            })
          );

        adminLogsList.sort((a, b) => {
          const aTime =
            a.createdAt?.seconds
              ? a.createdAt.seconds
              : 0;

          const bTime =
            b.createdAt?.seconds
              ? b.createdAt.seconds
              : 0;

          return bTime - aTime;
        });

        /*
         * Reports now have their own authoritative
         * collection.
         *
         * Do not depend on the old auction-level
         * reportReason / reportedAt summary fields.
         */
        const reportsSnapshot =
          await getDocs(
            collection(db, "reports")
          );

        const reportsList =
          reportsSnapshot.docs.map(
            (reportDoc) => ({
              id: reportDoc.id,
              ...reportDoc.data(),
            })
          );

          setUsers(usersList);
          setAuctions(auctionsList);
          setAdminLogs(
            adminLogsList
          );
          setReports(
            reportsList
          );
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [adminUser]);

  useEffect(() => {
    if (!adminUser) return;

    const unsubscribeTransactions =
      onSnapshot(
        collection(
          db,
          "stg_walletTransactions"
        ),
        (snapshot) => {
          const transactionsList =
          snapshot.docs.map(
            (transactionDoc) => ({
              id: transactionDoc.id,
              ...transactionDoc.data(),
            })
          );

        transactionsList.sort(
          (a, b) =>
            getTransactionTime(b) -
            getTransactionTime(a)
        );

        setTransactions(
          transactionsList
        );
        },
        (error) => {
          console.error(
            "Error listening to wallet transactions:",
            error
          );
        }
      );

    return () => {
      unsubscribeTransactions();
    };
  }, [adminUser]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
  
    if (!loginEmail.trim() || !loginPassword.trim()) {
      setLoginError("Please enter your email and password.");
      return;
    }
  
    try {
      setLoginError("");
      setMfaResolver(null);
      setMfaLoginCode("");
  
      await signInWithEmailAndPassword(
        auth,
        loginEmail.trim(),
        loginPassword
      );
    } catch (error) {
      console.error("Admin login error:", error);
  
      if (error?.code === "auth/multi-factor-auth-required") {
        try {
          const resolver = getMultiFactorResolver(auth, error);
  
          setMfaResolver(resolver);
          setLoginError("");
          return;
        } catch (resolverError) {
          console.error(
            "Could not prepare MFA verification:",
            resolverError
          );
  
          setLoginError(
            "Could not prepare two-factor authentication."
          );
          return;
        }
      }
  
      setLoginError(
        "Login failed. Please check your email and password."
      );
    }
  };

  const handleSendEnrollmentSms = async (e) => {
    e.preventDefault();
  
    const cleanPhone =
      String(mfaPhoneNumber || "").trim();
  
    if (!cleanPhone.startsWith("+")) {
      setMfaEnrollmentError(
        "Enter the phone number in international format, for example +965XXXXXXXX."
      );
      return;
    }
  
    const currentUser = auth.currentUser;
  
    if (!currentUser) {
      setMfaEnrollmentError(
        "Your admin session expired. Please sign in again."
      );
      return;
    }
  
    try {
      setMfaEnrollmentLoading(true);
      setMfaEnrollmentError("");
  
      const recaptchaVerifier =
  new RecaptchaVerifier(
    auth,
    "admin-mfa-recaptcha",
    undefined
  );
  
      const multiFactorSession =
        await multiFactor(currentUser).getSession();
  
      const phoneInfoOptions = {
        phoneNumber: cleanPhone,
        session: multiFactorSession,
      };
  
      const phoneAuthProvider =
        new PhoneAuthProvider(auth);
  
      const verificationId =
        await phoneAuthProvider.verifyPhoneNumber(
          phoneInfoOptions,
          recaptchaVerifier
        );
  
      setMfaEnrollmentVerificationId(
        verificationId
      );
  
      setMfaEnrollmentCode("");
      setMfaEnrollmentCodeSent(true);
      setMfaEnrollmentError("");
  
      recaptchaVerifier.clear();
    } catch (error) {
      console.error(
        "Send admin MFA SMS error:",
        error
      );
  
      setMfaEnrollmentError(
        error?.message ||
          "Could not send the SMS verification code."
      );
    } finally {
      setMfaEnrollmentLoading(false);
    }
  };
  
  const handleSmsEnrollment = async (e) => {
    e.preventDefault();
  
    const cleanCode =
      String(mfaEnrollmentCode || "")
        .replace(/\s/g, "")
        .trim();
  
    if (!mfaEnrollmentVerificationId) {
      setMfaEnrollmentError(
        "Send the SMS verification code first."
      );
      return;
    }
  
    if (!/^\d{6}$/.test(cleanCode)) {
      setMfaEnrollmentError(
        "Please enter the 6-digit SMS code."
      );
      return;
    }
  
    const currentUser = auth.currentUser;
  
    if (!currentUser) {
      setMfaEnrollmentError(
        "Your admin session expired. Please sign in again."
      );
      return;
    }
  
    try {
      setMfaEnrollmentLoading(true);
      setMfaEnrollmentError("");
  
      const credential =
        PhoneAuthProvider.credential(
          mfaEnrollmentVerificationId,
          cleanCode
        );
  
      const assertion =
        PhoneMultiFactorGenerator.assertion(
          credential
        );
  
      await multiFactor(currentUser).enroll(
        assertion,
        "X-AUCTIONS Admin SMS"
      );
  
      setMfaEnrollmentRequired(false);
      setMfaPhoneNumber("");
      setMfaEnrollmentCode("");
      setMfaEnrollmentVerificationId("");
      setMfaEnrollmentCodeSent(false);
      setMfaEnrollmentError("");
  
      setAdminUser(currentUser);
    } catch (error) {
      console.error(
        "SMS MFA enrollment error:",
        error
      );
  
      if (
        error?.code === "auth/invalid-verification-code"
        ||
        error?.code === "auth/code-expired"
      ) {
        setMfaEnrollmentError(
          "That SMS code is invalid or expired. Request a new code and try again."
        );
      } else {
        setMfaEnrollmentError(
          error?.message ||
            "Could not enable SMS two-factor authentication."
        );
      }
    } finally {
      setMfaEnrollmentLoading(false);
    }
  };

  const handleSendMfaLoginSms = async () => {
    if (!mfaResolver) {
      setLoginError(
        "Two-factor login session is missing. Please sign in again."
      );
      return;
    }
  
    try {
      setLoginError("");
      setMfaLoginCode("");
      setMfaLoginVerificationId("");
  
      const phoneHint =
        mfaResolver.hints.find(
          (hint) =>
            hint.factorId ===
            PhoneMultiFactorGenerator.FACTOR_ID
        );
  
      if (!phoneHint) {
        setLoginError(
          "No SMS authentication factor was found for this admin account."
        );
        return;
      }
  
      const recaptchaVerifier =
  new RecaptchaVerifier(
    auth,
    "admin-mfa-login-recaptcha",
    undefined
  );
  
      const phoneInfoOptions = {
        multiFactorHint: phoneHint,
        session: mfaResolver.session,
      };
  
      const phoneAuthProvider =
        new PhoneAuthProvider(auth);
  
      const verificationId =
        await phoneAuthProvider.verifyPhoneNumber(
          phoneInfoOptions,
          recaptchaVerifier
        );
  
      setMfaLoginVerificationId(
        verificationId
      );
  
      setLoginError("");
  
      recaptchaVerifier.clear();
    } catch (error) {
      console.error(
        "Send admin login MFA SMS error:",
        error
      );
  
      setLoginError(
        error?.message ||
          "Could not send the two-factor SMS code."
      );
    }
  };
  
  const handleSmsMfaLogin = async (e) => {
    e.preventDefault();
  
    if (!mfaResolver) {
      setLoginError(
        "Two-factor login session is missing. Please sign in again."
      );
      return;
    }
  
    if (!mfaLoginVerificationId) {
      setLoginError(
        "Please send the SMS verification code first."
      );
      return;
    }
  
    const cleanCode =
      String(mfaLoginCode || "")
        .replace(/\s/g, "")
        .trim();
  
    if (!/^\d{6}$/.test(cleanCode)) {
      setLoginError(
        "Please enter the 6-digit SMS code."
      );
      return;
    }
  
    try {
      setLoginError("");
  
      const credential =
        PhoneAuthProvider.credential(
          mfaLoginVerificationId,
          cleanCode
        );
  
      const assertion =
        PhoneMultiFactorGenerator.assertion(
          credential
        );
  
      await mfaResolver.resolveSignIn(
        assertion
      );
  
      setMfaResolver(null);
      setMfaLoginCode("");
      setMfaLoginVerificationId("");
      setLoginError("");
    } catch (error) {
      console.error(
        "Admin SMS MFA login error:",
        error
      );
  
      if (
        error?.code === "auth/invalid-verification-code"
        ||
        error?.code === "auth/code-expired"
      ) {
        setLoginError(
          "That SMS code is invalid or expired. Request a new code and try again."
        );
      } else {
        setLoginError(
          error?.message ||
            "Two-factor authentication failed."
        );
      }
    }
  };

  const handleAdminLogout = async () => {
    await signOut(auth);
    setAdminUser(null);
  };

  const refreshAdminLogs = async () => {
    try {
      const adminLogsSnapshot = await getDocs(collection(db, "adminLogs"));
      const adminLogsList = adminLogsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      adminLogsList.sort((a, b) => {
        const aTime = a.createdAt?.seconds ? a.createdAt.seconds : 0;
        const bTime = b.createdAt?.seconds ? b.createdAt.seconds : 0;
        return bTime - aTime;
      });

      setAdminLogs(adminLogsList);
    } catch (error) {
      console.error("Refresh admin logs error:", error);
    }
  };

  const getUserLabel = (uid) => {
    const user = users.find((item) => item.uid === uid || item.id === uid);
    return user?.username || user?.email || uid || "—";
  };
  
  const isWithinGlobalDateRange = (dateValue) => {
    if (globalDateRange === "all") return true;
    if (!dateValue) return false;
  
    const itemTime = dateValue?.seconds
      ? dateValue.seconds * 1000
      : Number(dateValue || 0);
  
    if (!itemTime) return false;
  
    const now = Date.now();
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
  
    if (globalDateRange === "today") {
      return itemTime >= todayStart.getTime();
    }
  
    if (globalDateRange === "last7") {
      return itemTime >= now - 7 * 24 * 60 * 60 * 1000;
    }
  
    if (globalDateRange === "last30") {
      return itemTime >= now - 30 * 24 * 60 * 60 * 1000;
    }
  
    if (globalDateRange === "thisMonth") {
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);
      return itemTime >= monthStart.getTime();
    }
  
    return true;
  };
  
  const formatClosureReasonLabel = (reason) => {
    const labels = {
      winner_declared: "Winner Declared",
      creator_closed: "Closed By Creator",
      time_expired: "Expired By Time Limit",
      admin_closed: "Closed By Admin",
      closed_unknown: "Closed - Unknown Reason",
      not_closed: "Not Closed",
    };

    return labels[reason] || reason || "—";
  };

  const formatAuctionTimeRemaining = (auction) => {
    if (!auction?.endsAtMs) return "—";

    if (auction.status === "closed" || auction.status === "removed") {
      return "Ended";
    }

    const remainingMs = Number(auction.endsAtMs) - Date.now();

    if (remainingMs <= 0) return "Expired";

    const totalSeconds = Math.floor(remainingMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}m ${seconds}s`;
  };
  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return "";
  
    const stringValue = String(value).replace(/"/g, '""');
  
    if (
      stringValue.includes(",") ||
      stringValue.includes('"') ||
      stringValue.includes("\n")
    ) {
      return `"${stringValue}"`;
    }
  
    return stringValue;
  };
  
  const downloadCsvFile = (fileName, rows) => {
    if (!rows || rows.length === 0) {
      alert("No data available to export.");
      return;
    }
  
    const headers = Object.keys(rows[0]);
  
    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        headers.map((header) => escapeCsvValue(row[header])).join(",")
      ),
    ].join("\n");
  
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
  
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
  
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  
    URL.revokeObjectURL(url);
  };

  const getTransactionUserKey = (transaction) =>
    transaction.uid || transaction.userId || transaction.email || "";
  
  const getTransactionTime = (transaction) => {
    if (transaction?.createdAt?.seconds) {
      return (
        Number(
          transaction.createdAt.seconds
        ) * 1000
      );
    }

    return Number(
      transaction?.createdAtMs ||
        transaction?.createdAt ||
        transaction?.timestamp ||
        0
    );
  };
  
  const isWalletResetTransaction = (transaction) => {
    const transactionType = String(transaction.type || "").toLowerCase();
    return transactionType.includes("wallet_reset");
  };
  
  const isFreeGrantTransaction = (transaction) => {
    const transactionType = String(transaction.type || "").toLowerCase();
  
    return (
      transactionType.includes("promo") ||
      transactionType.includes("refund") ||
      transactionType.includes("admin_credit") ||
      transactionType.includes("admin_refund_credit") ||
      transactionType.includes("admin_promotional_credit") ||
      transactionType.includes("add_balance")
    );
  };
  
  const buildTransactionAllocationMap = (transactionList) => {
    const balancesByUser = {};
    const allocationMap = {};
const matchedPaidTopupIds = new Set();

const sortedTransactions = [...transactionList].sort(
  (a, b) => getTransactionTime(a) - getTransactionTime(b)
);

sortedTransactions.forEach((transaction, index) => {
      const transactionId = transaction.id;
      const userKey = getTransactionUserKey(transaction);
      const amount = Number(transaction.amountKWD || transaction.amount || 0);
      const isSucceeded = transaction.status === "succeeded";
      const isFreeGrant = isFreeGrantTransaction(transaction);
  
      if (!transactionId || !userKey) return;
  
      if (!balancesByUser[userKey]) {
        balancesByUser[userKey] = {
          paidBalance: 0,
          freeBalance: 0,
        };
      }
  
      if (!isSucceeded) {
        allocationMap[transactionId] = {
          label: "Excluded / Other",
          impact: 0,
          paidImpact: 0,
          freeImpact: 0,
        };
        return;
      }
      
      if (isWalletResetTransaction(transaction)) {
        balancesByUser[userKey].paidBalance = 0;
        balancesByUser[userKey].freeBalance = 0;
      
        allocationMap[transactionId] = {
          label: "Wallet Reset",
          impact: 0,
          paidImpact: 0,
          freeImpact: 0,
        };
        return;
      }
  
      if (amount > 0 && isFreeGrant) {
        balancesByUser[userKey].freeBalance += amount;
  
        allocationMap[transactionId] = {
          label: "Free Grant",
          impact: amount,
          paidImpact: 0,
          freeImpact: amount,
        };
        return;
      }
  
      if (amount > 0 && !isFreeGrant) {
        balancesByUser[userKey].paidBalance += amount;
  
        allocationMap[transactionId] = {
          label: "Total Paid",
          impact: amount,
          paidImpact: amount,
          freeImpact: 0,
        };
        return;
      }
  
      if (amount < 0 && !isFreeGrant) {
        const consumptionAmount = Math.abs(amount);
        const sourceType = String(transaction.sourceType || "").toLowerCase();
      
        if (sourceType === "paid" || sourceType === "free" || sourceType === "mixed") {
  const paidUsed = Number(transaction.paidAmountKWD || 0);
  const freeUsed = Number(transaction.freeAmountKWD || 0);

  balancesByUser[userKey].paidBalance = Math.max(
    Number(balancesByUser[userKey].paidBalance || 0) - paidUsed,
    0
  );

  balancesByUser[userKey].freeBalance = Math.max(
    Number(balancesByUser[userKey].freeBalance || 0) - freeUsed,
    0
  );

  allocationMap[transactionId] = {
    label:
      paidUsed > 0 && freeUsed > 0
        ? "Mixed Revenue / Free Grant"
        : paidUsed > 0
        ? "Revenue"
        : "Free Grant Consumed",
    impact: paidUsed,
    paidImpact: paidUsed,
    freeImpact: freeUsed,
  };
  return;
}

const immediatePaidTopup = sortedTransactions
  .slice(0, index)
  .reverse()
  .find((previousTransaction) => {
    const previousId = previousTransaction.id;
    const previousUserKey = getTransactionUserKey(previousTransaction);
    const previousAmount = Number(
      previousTransaction.amountKWD ||
      previousTransaction.amount ||
      0
    );
    const previousType = String(previousTransaction.type || "").toLowerCase();
    const previousStatus = previousTransaction.status;
    const previousTime = getTransactionTime(previousTransaction);
    const currentTime = getTransactionTime(transaction);

    const previousIsFreeGrant =
      previousType.includes("promo") ||
      previousType.includes("refund") ||
      previousType.includes("admin_credit") ||
      previousType.includes("admin_refund_credit") ||
      previousType.includes("admin_promotional_credit") ||
      previousType.includes("add_balance");

    return (
      previousId &&
      !matchedPaidTopupIds.has(previousId) &&
      previousUserKey === userKey &&
      previousStatus === "succeeded" &&
      previousAmount === consumptionAmount &&
      previousAmount > 0 &&
      !previousIsFreeGrant &&
      currentTime - previousTime >= 0 &&
      currentTime - previousTime <= 5 * 60 * 1000
    );
  });

if (immediatePaidTopup) {
  matchedPaidTopupIds.add(immediatePaidTopup.id);

  balancesByUser[userKey].paidBalance = Math.max(
    Number(balancesByUser[userKey].paidBalance || 0) - consumptionAmount,
    0
  );

  allocationMap[transactionId] = {
    label: "Revenue",
    impact: consumptionAmount,
    paidImpact: consumptionAmount,
    freeImpact: 0,
  };
  return;
}
      
        const freeUsed = Math.min(
          balancesByUser[userKey].freeBalance,
          consumptionAmount
        );
      
        balancesByUser[userKey].freeBalance -= freeUsed;
      
        const remainingConsumption = consumptionAmount - freeUsed;
      
        const paidUsed = Math.min(
          balancesByUser[userKey].paidBalance,
          remainingConsumption
        );
      
        balancesByUser[userKey].paidBalance -= paidUsed;
      
        const adjustedFreeUsed =
  paidUsed === 0 && freeUsed === 0
    ? consumptionAmount
    : freeUsed;

allocationMap[transactionId] = {
  label:
    paidUsed > 0 && adjustedFreeUsed > 0
      ? "Mixed Revenue / Free Grant"
      : paidUsed > 0
      ? "Revenue"
      : "Free Grant Consumed",
  impact: paidUsed,
  paidImpact: paidUsed,
  freeImpact: adjustedFreeUsed,
};
return;
      }
  
      allocationMap[transactionId] = {
        label: "Excluded / Other",
        impact: 0,
        paidImpact: 0,
        freeImpact: 0,
      };
    });
  
    return allocationMap;
  };
  
  const transactionAllocationMap = useMemo(
    () => buildTransactionAllocationMap(transactions),
    [transactions]
  );
  
  const getTransactionAllocation = useCallback((transaction) => {
    return (
      transactionAllocationMap[transaction.id] || {
        label: "Excluded / Other",
        impact: 0,
        paidImpact: 0,
        freeImpact: 0,
      }
    );
  }, [transactionAllocationMap]);

  const filteredUsers = useMemo(() => {
    const filtered = users.filter((user) => {
      const searchText = userSearch.toLowerCase().trim();

      const matchesSearch =
        !searchText ||
        String(user.username || "").toLowerCase().includes(searchText) ||
        String(user.email || "").toLowerCase().includes(searchText) ||
        String(user.phoneNumber || "").toLowerCase().includes(searchText);

        const matchesArchiveFilter =
        userPhoneFilter === "archived"
          ? user.archived === true
          : user.archived !== true;

          const matchesPhoneFilter =
          userPhoneFilter === "all" ||
          userPhoneFilter === "archived" ||
          (userPhoneFilter === "verified" && user.phoneVerified === true) ||
          (userPhoneFilter === "notVerified" && user.phoneVerified !== true) ||
          (userPhoneFilter === "admins" && user.isAdmin === true) ||
          (userPhoneFilter === "banned" && user.banned === true) ||
          (userPhoneFilter === "disabled" && user.disabled === true) ||
          (userPhoneFilter === "walletPositive" &&
            Number(user.walletBalanceKWD || 0) > 0) ||
          (userPhoneFilter === "walletNegative" &&
            Number(user.walletBalanceKWD || 0) < 0) ||
          (userPhoneFilter === "hasCredits" &&
            Number(user.credits?.["15"] || 0) +
              Number(user.credits?.["30"] || 0) +
              Number(user.credits?.["60"] || 0) +
              Number(user.credits?.ext15 || 0) >
              0);

          return matchesSearch && matchesPhoneFilter && matchesArchiveFilter;
    });

    if (userSortMode === "walletHigh") {
      filtered.sort(
        (a, b) =>
          Number(b.walletBalanceKWD || 0) -
          Number(a.walletBalanceKWD || 0)
      );
    }

    if (userSortMode === "signupDate") {
      filtered.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;

        return bTime - aTime;
      });
    }

    if (userSortMode === "creditsHigh") {
      filtered.sort((a, b) => {
        const aCredits =
          Number(a.credits?.["15"] || 0) +
          Number(a.credits?.["30"] || 0) +
          Number(a.credits?.["60"] || 0) +
          Number(a.credits?.ext15 || 0);

        const bCredits =
          Number(b.credits?.["15"] || 0) +
          Number(b.credits?.["30"] || 0) +
          Number(b.credits?.["60"] || 0) +
          Number(b.credits?.ext15 || 0);

        return bCredits - aCredits;
      });
    }

    return filtered;
  }, [users, userSearch, userPhoneFilter, userSortMode]);

  const dateFilteredAuctions = useMemo(() => {
    return auctions.filter((auction) =>
      isWithinGlobalDateRange(auction.createdAt)
    );
  }, [auctions, globalDateRange]);
  
  const filteredAuctions = useMemo(() => {
    return dateFilteredAuctions
      .filter((auction) => {
        const matchesStatus =
        auctionStatusFilter === "all" ||
        (auctionStatusFilter === "live" && auction.live === true) ||
        (auctionStatusFilter === "flagged" && auction.flagged === true) ||
        (auctionStatusFilter === "reported" &&
          (auction.reported === true ||
            Number(auction.reportCount || 0) > 0)) ||
        auction.status === auctionStatusFilter;
  
        const searchText = auctionSearch.toLowerCase().trim();
  
        const sellerUsername =
        String(auction.createdByUsername || "").toLowerCase();
  
      const sellerEmail =
        String(auction.createdBy || "").toLowerCase();
  
      const matchesSearch =
        !searchText ||
        sellerUsername === searchText ||
        sellerEmail === searchText ||
        String(auction.title || "")
          .toLowerCase()
          .includes(searchText) ||
        String(auction.category || "")
          .toLowerCase()
          .includes(searchText);
  
        return matchesStatus && matchesSearch;
      })
      .sort((a, b) => {
        const aTime = a.createdAt?.seconds
          ? a.createdAt.seconds * 1000
          : Number(a.createdAt || 0);
  
        const bTime = b.createdAt?.seconds
          ? b.createdAt.seconds * 1000
          : Number(b.createdAt || 0);
  
        return bTime - aTime;
      });
    }, [dateFilteredAuctions, auctionStatusFilter, auctionSearch]);

    const handleExportSellerRatingsCsv = () => {
      const rows = ratedSellers
        .slice()
        .sort((a, b) => {
          const ratingDifference =
            Number(b.averageRating || 0) -
            Number(a.averageRating || 0);
  
          if (ratingDifference !== 0) {
            return ratingDifference;
          }
  
          return (
            Number(b.ratingCount || 0) -
            Number(a.ratingCount || 0)
          );
        })
        .map((user, index) => ({
          rank: index + 1,
  
          userId:
            user.id || "",
  
          uid:
            user.uid || "",
  
          username:
            user.username || "",
  
          email:
            user.email || "",
  
          averageRating:
            Number(
              user.averageRating || 0
            ).toFixed(2),
  
          ratingCount:
            Number(
              user.ratingCount || 0
            ),
  
          ratingUpdatedAt:
            user.ratingUpdatedAt?.seconds
              ? new Date(
                  user.ratingUpdatedAt
                    .seconds * 1000
                ).toLocaleString()
              : user.ratingUpdatedAtMs
              ? new Date(
                  Number(
                    user.ratingUpdatedAtMs
                  )
                ).toLocaleString()
              : "",
  
          auctionsCreated:
            auctions.filter(
              (auction) =>
                auction.createdBy ===
                  user.email ||
                auction.createdByUsername ===
                  user.username
            ).length,
        }));
  
      downloadCsvFile(
        "seller-ratings-export.csv",
        rows
      );
    };

  const handleExportUsersCsv = () => {
    const rows = users.map((user) => ({
      userId: user.id || "",
      uid: user.uid || "",
      username: user.username || "",
      email: user.email || "",
      phoneNumber: user.phoneNumber || "",
      phoneVerified: user.phoneVerified ? "Yes" : "No",
      walletBalanceKWD: Number(user.walletBalanceKWD || 0).toFixed(3),
      credit15: Number(user.credits?.["15"] || 0),
      credit30: Number(user.credits?.["30"] || 0),
      credit60: Number(user.credits?.["60"] || 0),
      creditExt15: Number(user.credits?.ext15 || 0),
      isAdmin: user.isAdmin ? "Yes" : "No",
      banned: user.banned ? "Yes" : "No",
      disabled: user.disabled ? "Yes" : "No",
      archived: user.archived ? "Yes" : "No",
      signupDate: user.createdAt?.seconds
        ? new Date(user.createdAt.seconds * 1000).toLocaleString()
        : "",
      lastAdminNote: user.lastAdminNote || "",
    }));
  
    downloadCsvFile("users-export.csv", rows);
  };

  const handleExportAuctionsCsv = () => {
    const rows = auctions.map((auction) => ({
      auctionId: auction.id || "",
      title: auction.title || "",
      category: auction.category || "",
      seller: auction.createdByUsername || "",
      sellerEmail: auction.createdBy || "",
      status: auction.status || "",
      live: auction.live ? "Yes" : "No",
      createdAt: auction.createdAt?.seconds
        ? new Date(auction.createdAt.seconds * 1000).toLocaleString()
        : auction.createdAt
        ? new Date(Number(auction.createdAt)).toLocaleString()
        : "",
      currentBid: Number(auction.currentBid || 0).toFixed(3),
      totalRevenue: (
        Number(auction.billingAmountKWD || 0) +
        Number(auction.extensionTotalAmountKWD || 0)
      ).toFixed(3),
      reports: Number(auction.reportCount || 0),
      winner: auction.currentWinner || auction.winner || "",
      durationMinutes: auction.durationMinutes || "",
      lastAdminNote: auction.lastAdminNote || "",
    }));
  
    downloadCsvFile("auctions-export.csv", rows);
  };

  const dateFilteredTransactions = useMemo(() => {
    return transactions.filter((transaction) =>
      isWithinGlobalDateRange(
        getTransactionTime(transaction)
      )
    );
  }, [transactions, globalDateRange]);
  
  const filteredTransactions = useMemo(() => {
    return dateFilteredTransactions
      .filter((transaction) => {
        if (transactionStatusFilter === "all") return true;
        return transaction.status === transactionStatusFilter;
      })
      .sort(
        (a, b) =>
          getTransactionTime(b) -
          getTransactionTime(a)
      );
  }, [dateFilteredTransactions, transactionStatusFilter]);

  const handleExportRevenueCsv = () => {
    const rows = auctions.map((auction) => {
      const initialRevenue = Number(auction.billingAmountKWD || 0);
      const extensionRevenue = Number(auction.extensionTotalAmountKWD || 0);
      const totalRevenue = initialRevenue + extensionRevenue;
  
      return {
        auctionId: auction.id || "",
        title: auction.title || "",
        seller: auction.createdByUsername || "",
        sellerEmail: auction.createdBy || "",
        status: auction.status || "",
        durationMinutes: auction.durationMinutes || "",
        billingType: auction.billingType || "",
        initialRevenueKWD: initialRevenue.toFixed(3),
        extensionRevenueKWD: extensionRevenue.toFixed(3),
        totalRevenueKWD: totalRevenue.toFixed(3),
        extensionCount: Number(auction.extensionCount || 0),
        createdAt: auction.createdAt?.seconds
          ? new Date(auction.createdAt.seconds * 1000).toLocaleString()
          : auction.createdAt
          ? new Date(Number(auction.createdAt)).toLocaleString()
          : "",
        closedAt: auction.closedAt
          ? new Date(Number(auction.closedAt)).toLocaleString()
          : "",
        billingNote: auction.billingNote || "",
        lastExtensionBillingNote: auction.lastExtensionBillingNote || "",
      };
    });
  
    downloadCsvFile("revenue-export.csv", rows);
  };

  const handleExportPaymentsCsv = () => {
    const rows = transactions.map((transaction) => {
      const allocation = getTransactionAllocation(transaction);
  
      return {
        transactionId: transaction.id || "",
        user: getUserLabel(transaction.uid),
        uid: transaction.uid || "",
        type: transaction.type || "",
        amountKWD: Number(transaction.amountKWD || 0).toFixed(3),
        allocation: allocation.label,
        impactKWD: Number(allocation.impact || 0).toFixed(3),
        credit: transaction.durationCredit || "",
        status: transaction.status || "",
        provider: transaction.provider || "",
        reference: transaction.providerRef || transaction.invoiceId || "",
        note: transaction.note || "",
        date: getTransactionTime(transaction)
        ? new Date(
            getTransactionTime(transaction)
          ).toLocaleString()
        : "",
      };
    });
  
    downloadCsvFile("payments-export.csv", rows);
  };

  const dateFilteredAdminLogs = useMemo(() => {
    return adminLogs.filter((log) =>
      isWithinGlobalDateRange(log.createdAt || log.createdAtText)
    );
  }, [adminLogs, globalDateRange]);
  
  const filteredAdminLogs = useMemo(() => {
    return dateFilteredAdminLogs.filter((log) => {
      const searchText = adminLogSearch.toLowerCase().trim();

      const matchesSearch =
        !searchText ||
        String(log.action || "").toLowerCase().includes(searchText) ||
        String(log.adminEmail || "").toLowerCase().includes(searchText) ||
        String(log.targetUsername || "").toLowerCase().includes(searchText) ||
        String(log.targetEmail || "").toLowerCase().includes(searchText) ||
        String(log.targetTitle || "").toLowerCase().includes(searchText) ||
        String(log.targetOwner || "").toLowerCase().includes(searchText) ||
        String(log.targetTransactionUser || "").toLowerCase().includes(searchText);

      const matchesAction =
        adminLogActionFilter === "all" || log.action === adminLogActionFilter;

      return matchesSearch && matchesAction;
    });
  }, [dateFilteredAdminLogs, adminLogSearch, adminLogActionFilter]);

  const handleExportTopFreeGrantUsersCsv = () => {
    const rows = topFreeGrantUsers.map((user) => ({
      username: user.username || "",
      email: user.email || "",
      freeGrantsCount: Number(user.freeGrantCount || 0),
      freeGrantsValueKWD: Number(user.freeGrants || 0).toFixed(3),
      walletBalanceKWD: Number(user.walletBalance || 0).toFixed(3),
      credit15: Number(user.credits?.["15"] || 0),
      credit30: Number(user.credits?.["30"] || 0),
      credit60: Number(user.credits?.["60"] || 0),
      creditExt15: Number(user.credits?.ext15 || 0),
      auctionsCreated: Number(user.auctionsCreated || 0),
      lastFreeGrant: user.lastFreeGrant
        ? new Date(Number(user.lastFreeGrant)).toLocaleString()
        : "",
    }));
  
    downloadCsvFile("top-free-grant-users-export.csv", rows);
  };

  const handleExportTopBuyersCsv = () => {
    const rows = topBuyers.map((buyer) => ({
      buyerName: buyer.buyerName || "",
      email: buyer.email || "",
      auctionsWon: Number(buyer.auctionsWon || 0),
      totalWinningValueKWD: Number(buyer.totalWinningValue || 0).toFixed(3),
      latestWonAuction: buyer.latestWonAuction || "",
      lastWinDate: buyer.lastWinDate
        ? new Date(Number(buyer.lastWinDate)).toLocaleString()
        : "",
    }));
  
    downloadCsvFile("top-buyers-export.csv", rows);
  };

  const handleExportTopPayingUsersCsv = () => {
    const rows = topPayingUsers.map((user) => ({
      username: user.username || "",
      email: user.email || "",
      transactions: Number(user.transactionCount || 0),
      totalPaidKWD: Number(user.totalPaid || 0).toFixed(3),
      revenueKWD: Number(user.revenueGenerated || 0).toFixed(3),
      freeGrantsKWD: Number(user.freeCredits || 0).toFixed(3),
      walletKWD: Number(user.walletBalance || 0).toFixed(3),
      auctionsCreated: Number(user.auctionsCreated || 0),
      lastTopUp: user.lastPayment
        ? new Date(Number(user.lastPayment)).toLocaleString()
        : "",
    }));
  
    downloadCsvFile("top-paying-users-export.csv", rows);
  };

  const handleExportAdminLogsCsv = () => {
    const rows = filteredAdminLogs.map((log) => ({
      logId: log.id || "",
      action: formatAdminActionLabel(log.action),
      rawAction: log.action || "",
      adminEmail: log.adminEmail || "",
      target:
        log.targetUsername ||
        log.targetTitle ||
        log.targetOwner ||
        log.targetTransactionUser ||
        log.targetUid ||
        log.targetId ||
        "",
      targetEmail: log.targetEmail || log.targetOwnerEmail || "",
      targetIdOrUid: log.targetUid || log.targetId || "",
      targetType: log.targetType || "",
      details: formatAdminLogDetails(log.details),
      date: log.createdAtText || "",
    }));
  
    downloadCsvFile("admin-logs-export.csv", rows);
  };

  const totalWalletBalance = users.reduce(
    (sum, user) => sum + Number(user.walletBalanceKWD || 0),
    0
  );

  const totalPaidWalletLiability = users.reduce(
    (sum, user) => sum + Number(user.paidWalletBalanceKWD || 0),
    0
  );
  
  const totalFreeWalletLiability = users.reduce(
    (sum, user) => sum + Number(user.freeWalletBalanceKWD || 0),
    0
  );

  const averageWalletPerUser =
  users.length > 0
    ? totalWalletBalance / users.length
    : 0;

    const usersWithWalletBalance = users.filter(
      (user) => Number(user.walletBalanceKWD || 0) > 0
    );

    const topWalletUser =
      usersWithWalletBalance.length > 0
        ? [...usersWithWalletBalance].sort(
            (a, b) =>
              Number(b.walletBalanceKWD || 0) -
              Number(a.walletBalanceKWD || 0)
          )[0]
        : null;

      const topCreditHolder =
      users.length > 0
        ? [...users].sort((a, b) => {
            const aCredits =
              Number(a.credits?.["15"] || 0) +
              Number(a.credits?.["30"] || 0) +
              Number(a.credits?.["60"] || 0) +
              Number(a.credits?.ext15 || 0);

            const bCredits =
              Number(b.credits?.["15"] || 0) +
              Number(b.credits?.["30"] || 0) +
              Number(b.credits?.["60"] || 0) +
              Number(b.credits?.ext15 || 0);

            return bCredits - aCredits;
          })[0]
        : null;

    const topCreditHolderTotal =
      topCreditHolder
        ? Number(topCreditHolder.credits?.["15"] || 0) +
          Number(topCreditHolder.credits?.["30"] || 0) +
          Number(topCreditHolder.credits?.["60"] || 0) +
          Number(topCreditHolder.credits?.ext15 || 0)
        : 0;

        const ratedSellers = users
        .filter(
          (user) =>
            Number(user.ratingCount || 0) > 0 &&
            Number(user.averageRating || 0) > 0
        );

      const topRatedSellers = [
        ...ratedSellers,
      ]
        .sort((a, b) => {
          const ratingDifference =
            Number(b.averageRating || 0) -
            Number(a.averageRating || 0);

          if (ratingDifference !== 0) {
            return ratingDifference;
          }

          return (
            Number(b.ratingCount || 0) -
            Number(a.ratingCount || 0)
          );
        })
        .slice(0, 10);

      const lowestRatedSellers = [
        ...ratedSellers,
      ]
        .sort((a, b) => {
          const ratingDifference =
            Number(a.averageRating || 0) -
            Number(b.averageRating || 0);

          if (ratingDifference !== 0) {
            return ratingDifference;
          }

          return (
            Number(b.ratingCount || 0) -
            Number(a.ratingCount || 0)
          );
        })
        .slice(0, 10);

      const highestRatedSeller =
        topRatedSellers.length > 0
          ? topRatedSellers[0]
          : null;

      const topBidAuction =
      auctions.length > 0
        ? [...auctions].sort(
            (a, b) =>
              Number(b.currentBid || 0) -
              Number(a.currentBid || 0)
          )[0]
        : null;

        const highestBidEverAuction =
        auctions.length > 0
          ? [...auctions].sort(
              (a, b) =>
                Number(b.currentBid || 0) -
                Number(a.currentBid || 0)
            )[0]
          : null;

          const sellerAuctionCounts = {};

          auctions.forEach((auction) => {
            const sellerKey =
              auction.createdBy ||
              "Unknown Seller";
    
            sellerAuctionCounts[sellerKey] =
              (sellerAuctionCounts[sellerKey] || 0) + 1;
          });
    
          const mostActiveSellerEntry =
            Object.entries(sellerAuctionCounts).length > 0
              ? Object.entries(sellerAuctionCounts).sort(
                  (a, b) => b[1] - a[1]
                )[0]
              : null;
    
          const mostActiveSeller =
            mostActiveSellerEntry
              ? users.find(
                  (user) =>
                    user.email === mostActiveSellerEntry[0]
                )
              : null;

            const mostReportedAuction =
            auctions.filter(
              (auction) => Number(auction.reportCount || 0) > 0
            ).length > 0
              ? [...auctions]
                  .filter(
                    (auction) => Number(auction.reportCount || 0) > 0
                  )
                  .sort(
                    (a, b) =>
                      Number(b.reportCount || 0) -
                      Number(a.reportCount || 0)
                  )[0]
              : null;

              const userRevenueMap = {};

              transactions.forEach((transaction) => {
                const uid = transaction.uid;
      
                if (!uid) return;
      
                const amount = Number(transaction.amountKWD || 0);
      
                if (amount <= 0) return;
      
                userRevenueMap[uid] =
                  (userRevenueMap[uid] || 0) + amount;
              });
      
              const highestRevenueEntry =
                Object.entries(userRevenueMap).length > 0
                  ? Object.entries(userRevenueMap).sort(
                      (a, b) => b[1] - a[1]
                    )[0]
                  : null;
      
              const highestRevenueUser =
                highestRevenueEntry
                  ? users.find(
                      (user) =>
                        user.uid === highestRevenueEntry[0] ||
                        user.id === highestRevenueEntry[0]
                    )
                  : null;

                  

                  const flaggedSellerCounts = {};

                  auctions
                    .filter((auction) => auction.flagged === true)
                    .forEach((auction) => {
                      const sellerKey =
                        auction.createdByUsername ||
                        auction.createdBy ||
                        "Unknown Seller";
          
                      flaggedSellerCounts[sellerKey] =
                        (flaggedSellerCounts[sellerKey] || 0) + 1;
                    });
          
                  const mostFlaggedSellerEntry =
                    Object.entries(flaggedSellerCounts).length > 0
                      ? Object.entries(flaggedSellerCounts).sort(
                          (a, b) => b[1] - a[1]
                        )[0]
                      : null;
          
                  const mostFlaggedSeller =
                    mostFlaggedSellerEntry
                      ? users.find(
                          (user) =>
                            user.username === mostFlaggedSellerEntry[0] ||
                            user.email === mostFlaggedSellerEntry[0]
                        )
                      : null;

                      const buyerStatsMap = {};

                      dateFilteredAuctions
                      .filter((auction) => auction.currentWinner || auction.winner)
                      .forEach((auction) => {
                          const buyerName = auction.currentWinner || auction.winner || "Unknown Buyer";
              
                          if (!buyerStatsMap[buyerName]) {
                            buyerStatsMap[buyerName] = {
                              buyerName,
                              email: "",
                              auctionsWon: 0,
                              totalWinningValue: 0,
                              latestWonAuction: "",
                              lastWinDate: 0,
                            };
                          }
              
                          buyerStatsMap[buyerName].auctionsWon += 1;
                          buyerStatsMap[buyerName].totalWinningValue += Number(auction.currentBid || 0);
                          buyerStatsMap[buyerName].latestWonAuction = auction.title || "Untitled Auction";
                          buyerStatsMap[buyerName].lastWinDate =
                            auction.closedAt ||
                            auction.endedAt ||
                            auction.createdAt?.seconds * 1000 ||
                            auction.createdAt ||
                            0;
              
                          const matchedUser = users.find(
                            (user) =>
                              user.username === buyerName ||
                              user.email === buyerName
                          );
              
                          buyerStatsMap[buyerName].email = matchedUser?.email || "";
                        });
              
                      const topBuyers = Object.values(buyerStatsMap)
                        .filter((buyer) => {
                          const searchText = buyerSearch.toLowerCase().trim();
              
                          if (!searchText) return true;
              
                          return (
                            String(buyer.buyerName || "").toLowerCase().includes(searchText) ||
                            String(buyer.email || "").toLowerCase().includes(searchText)
                          );
                        })
                        .sort((a, b) => b.auctionsWon - a.auctionsWon);

                        const payingUsersMap = {};

                        dateFilteredTransactions.forEach((transaction) => {
                          const userKey =
                            transaction.uid ||
                            transaction.userId ||
                            transaction.email;
                
                          if (!userKey) return;
                
                          if (!payingUsersMap[userKey]) {
                            const matchedUser = users.find(
                              (user) =>
                                user.uid === transaction.uid ||
                                user.id === transaction.uid ||
                                user.email === transaction.email
                            );
                
                            payingUsersMap[userKey] = {
                              username:
                                matchedUser?.username || "Unknown User",
                              email:
                                matchedUser?.email ||
                                transaction.email ||
                                "—",
                                transactionCount: 0,
                                totalPaid: 0,
                                revenueGenerated: 0,
                                walletBalance:
                                Number(matchedUser?.walletBalanceKWD || 0),
                              freeCredits: 0,
                              lastPayment: 0,
                              auctionsCreated: 0,
                            };
                
                            if (matchedUser) {
                              payingUsersMap[userKey].auctionsCreated =
                                auctions.filter(
                                  (auction) =>
                                    auction.createdBy === matchedUser.email ||
                                    auction.createdByUsername === matchedUser.username
                                ).length;
                            }
                          }
                
                          const transactionAmount = Number(
                            transaction.amountKWD ||
                            transaction.amount ||
                            0
                          );
                
                          const transactionType = String(
                            transaction.type || ""
                          ).toLowerCase();
                
                          const isFreeGrant =
                          transactionType.includes("promo") ||
                          transactionType.includes("refund") ||
                          transactionType.includes("admin") ||
                          transactionType.includes("add_balance") ||
                          transactionType.includes("balance");

                          const isRealPayment =
                          transaction.status === "succeeded" &&
                          transactionAmount > 0 &&
                          !isFreeGrant;

                          const transactionNote = String(
                            transaction.note || ""
                          ).toLowerCase();

                          const isRevenueConsumption =
                          transaction.status === "succeeded" &&
                          transactionAmount < 0 &&
                          !isFreeGrant;

                        if (isFreeGrant) {
                          payingUsersMap[userKey].freeCredits =
                            Number(
                              payingUsersMap[userKey].freeCredits || 0
                            ) + transactionAmount;
                        }

                        if (isRealPayment) {
                          payingUsersMap[userKey].transactionCount += 1;

                          payingUsersMap[userKey].totalPaid =
                            Number(
                              payingUsersMap[userKey].totalPaid || 0
                            ) + transactionAmount;

                            const paymentTime =
                            getTransactionTime(
                              transaction
                            );

                          if (
                            paymentTime >
                            payingUsersMap[userKey].lastPayment
                          ) {
                            payingUsersMap[userKey].lastPayment =
                              paymentTime;
                          }
                        }

                        const allocation = getTransactionAllocation(transaction);

                        if (allocation.label === "Revenue" || allocation.label === "Mixed Revenue / Free Grant") {
                        payingUsersMap[userKey].revenueGenerated =
                        Number(
                        payingUsersMap[userKey].revenueGenerated || 0
                        ) + Number(allocation.paidImpact || 0);
                        }
                        });
                
                        const topPayingUsers = Object.values(payingUsersMap)
                          .sort((a, b) => b.totalPaid - a.totalPaid);

                          const freeGrantUsersMap = {};

                          dateFilteredTransactions.forEach((transaction) => {
                            const transactionType = String(
                              transaction.type || ""
                            ).toLowerCase();
                  
                            const transactionAmount = Number(
                              transaction.amountKWD ||
                              transaction.amount ||
                              0
                            );
                  
                            const isFreeGrant =
                              transactionType.includes("promo") ||
                              transactionType.includes("refund");
                  
                            if (!isFreeGrant) return;
                  
                            const userKey =
                              transaction.uid ||
                              transaction.userId ||
                              transaction.email;
                  
                            if (!userKey) return;
                  
                            if (!freeGrantUsersMap[userKey]) {
                              const matchedUser = users.find(
                                (user) =>
                                  user.uid === transaction.uid ||
                                  user.id === transaction.uid ||
                                  user.email === transaction.email
                              );
                  
                              freeGrantUsersMap[userKey] = {
                                username:
                                  matchedUser?.username || "Unknown User",
                  
                                email:
                                  matchedUser?.email ||
                                  transaction.email ||
                                  "—",
                  
                                  freeGrants: 0,

                                  freeGrantCount: 0,
                    
                                  walletBalance:
                                  Number(matchedUser?.walletBalanceKWD || 0),
                  
                                credits:
                                  matchedUser?.credits || {},
                  
                                auctionsCreated: matchedUser
                                  ? auctions.filter(
                                      (auction) =>
                                        auction.createdBy === matchedUser.email
                                    ).length
                                  : 0,
                  
                                lastFreeGrant: 0,
                              };
                            }
                  
                            freeGrantUsersMap[userKey].freeGrants +=
                            transactionAmount;
                
                          freeGrantUsersMap[userKey].freeGrantCount += 1;
                  
                          const grantTime =
                          getTransactionTime(
                            transaction
                          );
              
                        if (
                          grantTime >
                          freeGrantUsersMap[userKey].lastFreeGrant
                        ) {
                          freeGrantUsersMap[userKey].lastFreeGrant =
                            grantTime;
                        }
                          });
                  
                          const topFreeGrantUsers =
                            Object.values(freeGrantUsersMap).sort(
                              (a, b) => b.freeGrants - a.freeGrants
                            );

                            const totalTransactionValue = dateFilteredTransactions.reduce(
                              (sum, transaction) => sum + Number(transaction.amountKWD || 0),
                              0
                            );
                          
                            const totalRevenue = topPayingUsers.reduce(
                              (sum, user) =>
                                sum + Number(user.revenueGenerated || 0),
                              0
                            );
                          
                            const totalPaidValue = topPayingUsers.reduce(
                              (sum, user) =>
                                sum + Number(user.totalPaid || 0),
                              0
                            );
                          
                            const unusedPaidValue = Math.max(
                              totalPaidValue - totalRevenue,
                              0
                            );
                          
                            const freeValueConsumed = dateFilteredTransactions.reduce((sum, transaction) => {
                              const transactionAmount = Number(
                                transaction.amountKWD || transaction.amount || 0
                              );
                            
                              if (transaction.status !== "succeeded") return sum;
                              if (transactionAmount >= 0) return sum;
                            
                              const allocation = getTransactionAllocation(transaction);
                            
                              if (
                                allocation.label !== "Free Grant Consumed" &&
                                allocation.label !== "Mixed Revenue / Free Grant"
                              ) {
                                return sum;
                              }
                            
                              return sum + Number(allocation.freeImpact || 0);
                            }, 0);

                            const paidRevenueReconciliationDifference =
  Number(totalPaidValue || 0) -
  Number(totalRevenue || 0) -
  Number(totalPaidWalletLiability || 0);

  const totalFreeGrantsGiven = dateFilteredTransactions
  .filter((transaction) => {
    const transactionType = String(transaction.type || "").toLowerCase();

    return (
      transactionType.includes("promo") ||
      transactionType.includes("refund") ||
      transactionType.includes("admin_credit") ||
      transactionType.includes("admin_refund_credit") ||
      transactionType.includes("admin_promotional_credit") ||
      transactionType.includes("add_balance")
    );
  })
  .reduce((sum, transaction) => {
    return (
      sum +
      Math.max(
        Number(transaction.amountKWD || transaction.amount || 0),
        0
      )
    );
  }, 0);

  const freeGrantReconciliationDifference =
  Number(totalFreeGrantsGiven || 0) -
  Number(freeValueConsumed || 0) -
  Number(totalFreeWalletLiability || 0);

  const totalAdminWalletAdjustments = transactions
    .filter(
      (transaction) =>
        transaction.type === "admin_credit"
    )
    .reduce(
      (sum, transaction) => sum + Number(transaction.amountKWD || 0),
      0
    );

    const totalRefundCredits = transactions.filter(
      (transaction) => transaction.type === "admin_refund_credit"
    ).length;

    const totalPromoCredits = transactions.filter(
      (transaction) => transaction.type === "admin_promotional_credit"
    ).length;

    const latestAdminLog =
dateFilteredAdminLogs.length > 0 ? dateFilteredAdminLogs[0] : null;

    const usersWithRealCreatedAt = users.filter(
      (user) => user.createdAt?.seconds
    );
  
    const newestUser =
      usersWithRealCreatedAt.length > 0
        ? [...usersWithRealCreatedAt].sort(
            (a, b) => b.createdAt.seconds - a.createdAt.seconds
          )[0]
        : null;

        const auctionsWithDates = auctions.filter(
          (auction) => auction.createdAt
        );
      
        const newestAuction =
          auctionsWithDates.length > 0
            ? [...auctionsWithDates].sort((a, b) => {
                const aTime = a.createdAt?.seconds
                  ? a.createdAt.seconds
                  : Number(a.createdAt || 0);
      
                const bTime = b.createdAt?.seconds
                  ? b.createdAt.seconds
                  : Number(b.createdAt || 0);
      
                return bTime - aTime;
              })[0]
            : null;
      
            const recentAuctions = [...auctions]
  .sort((a, b) => {
    const aTime = a.createdAt?.seconds
      ? a.createdAt.seconds * 1000
      : Number(a.createdAt || 0);

    const bTime = b.createdAt?.seconds
      ? b.createdAt.seconds * 1000
      : Number(b.createdAt || 0);

    return bTime - aTime;
  })
  .slice(0, 5);

  const recentTransactions = [...transactions]
  .sort(
    (a, b) =>
      getTransactionTime(b) -
      getTransactionTime(a)
  )
  .slice(0, 5);

  const recentReports =
  (() => {
    const getReportTime = (
      report
    ) => {
      if (
        report?.createdAt?.seconds
      ) {
        return (
          Number(
            report.createdAt.seconds
          ) * 1000
        );
      }

      return Number(
        report?.createdAtMs ||
          report?.timestamp ||
          report?.createdAt ||
          0
      );
    };

    /*
     * Dashboard should show the latest
     * reported AUCTIONS, rather than
     * duplicate rows when several users
     * report the same auction.
     */
    const latestByAuction =
      new Map();

    reports.forEach(
      (report) => {
        const auctionId =
          String(
            report?.auctionId ||
              ""
          ).trim();

        if (!auctionId) {
          return;
        }

        const reportTime =
          getReportTime(
            report
          );

        const existing =
          latestByAuction.get(
            auctionId
          );

        if (
          !existing ||
          reportTime >
            existing.reportTime
        ) {
          latestByAuction.set(
            auctionId,
            {
              report,
              reportTime,
            }
          );
        }
      }
    );

    return Array.from(
      latestByAuction.values()
    )
      .map(
        ({
          report,
          reportTime,
        }) => {
          const auction =
            auctions.find(
              (item) =>
                item.id ===
                report.auctionId
            );

          return {
            id:
              report.id,

            auctionId:
              report.auctionId,

            title:
              auction?.title ||
              report.auctionTitle ||
              "Untitled Auction",

            createdByUsername:
              auction
                ?.createdByUsername ||
              report
                .auctionCreator ||
              "",

            createdBy:
              auction?.createdBy ||
              "",

            status:
              auction?.status ||
              "unknown",

            reportReason:
              report?.reason ||
              "",

            reportedAt:
              report?.createdAt ||
              report?.createdAtMs ||
              report?.timestamp ||
              null,

            reportStatus:
              report?.resolved ===
                true
                ? "resolved"
                : report?.status ||
                  "pending",

            reportTime,
          };
        }
      )
      .sort(
        (a, b) =>
          b.reportTime -
          a.reportTime
      )
      .slice(
        0,
        5
      );
  })();

        const formatAdminLogDetails = (details) => {
          if (!details) return "—";

    return Object.entries(details)
      .map(([key, value]) => {
        const readableKey = key
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (char) => char.toUpperCase());

        if (typeof value === "object" && value !== null) {
          return `${readableKey}: ${JSON.stringify(value)}`;
        }

        if (typeof value === "boolean") {
          return `${readableKey}: ${value ? "Yes" : "No"}`;
        }

        return `${readableKey}: ${value}`;
      })
      .join(" | ");
  };

  const getAdminLogBadgeClass = (action) => {
    const redActions = [
      "ban_user",
      "disable_user",
      "remove_auction",
      "reset_wallet",
      "remove_admin_role",
      "archive_user",
    ];

    const greenActions = [
      "enable_user",
      "unban_user",
      "grant_admin_role",
      "unflag_auction",
      "unarchive_user",
    ];

    const orangeActions = [
      "flag_auction",
      "close_auction",
    ];

    const blueActions = [
      "refund_credit",
      "promotional_credit",
      "add_promotional_balance",
      "mark_transaction_manually",
    ];

    if (redActions.includes(action)) {
      return "badge failed";
    }

    if (greenActions.includes(action)) {
      return "badge succeeded";
    }

    if (orangeActions.includes(action)) {
      return "badge pending";
    }

    if (blueActions.includes(action)) {
      return "badge reviewed";
    }

    return "badge";
  };

  const formatAdminActionLabel = (action) => {
    const labels = {
      ban_user: "Ban User",
      unban_user: "Unban User",
      disable_user: "Disable User",
      enable_user: "Enable User",
      remove_auction: "Remove Auction",
      close_auction: "Close Auction",
      flag_auction: "Flag Auction",
      unflag_auction: "Unflag Auction",
      reset_wallet: "Reset Wallet",
      add_promotional_balance: "Add Balance",
      refund_credit: "Refund Credit",
      promotional_credit: "Promo Credit",
      grant_admin_role: "Grant Admin",
      remove_admin_role: "Remove Admin",
      archive_user: "Archive User",
      unarchive_user: "Unarchive User",
      mark_transaction_manually: "Mark Transaction",
    };

    return labels[action] || action || "Unknown Action";
  };

  const runAdminWalletAdjustment =
    async ({
      targetUid,
      action,
      amountKWD = 0,
      creditType = "",
      note = "",
    }) => {
      const currentUser =
        auth.currentUser;

      if (!currentUser) {
        throw new Error(
          "Admin session expired. Please sign in again."
        );
      }

      const idToken =
        await currentUser.getIdToken(
          true
        );

      const response =
        await fetch(
          "https://us-central1-q8auctionapp-2c6dd.cloudfunctions.net/adminWalletAdjustment",
          {
            method: "POST",

            headers: {
              Authorization:
                `Bearer ${idToken}`,

              "Content-Type":
                "application/json",
            },

            body:
              JSON.stringify({
                targetUid,
                action,
                amountKWD,
                creditType,
                note,
              }),
          }
        );

      const data =
        await response
          .json()
          .catch(
            () => ({})
          );

      if (
        !response.ok ||
        data?.ok !== true
      ) {
        throw new Error(
          data?.error ||
            "Admin wallet adjustment failed."
        );
      }

      return data;
    };


  const sendAdminNotificationToUser = async ({
    uid,
    title,
    body,
    type = "wallet",
    notificationKey = "",
    notificationType = "",
    notificationData = {},
  }) => {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) return false;

      const idToken = await currentUser.getIdToken();

      const response = await fetch(
        "https://us-central1-q8auctionapp-2c6dd.cloudfunctions.net/adminNotifyUser",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            uid,
            title,
            body,
            type,
            notificationKey,
            notificationType,
            notificationData,
            settingKey: type === "wallet" ? "wallet" : "",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.ok) {
        console.warn("Admin notification failed:", data);
        return false;
      }

      return true;
    } catch (error) {
      console.warn("Admin notification error:", error);
      return false;
    }
  };

  const writeAdminLog = async ({ action, targetType, targetId, details }) => {
    try {
      const now = Date.now();

      let targetUsername = "";
      let targetEmail = "";
      let targetTitle = "";
      let targetOwner = "";
      let targetOwnerEmail = "";
      let targetTransactionUser = "";

      if (targetType === "user") {
        const targetUser = users.find((user) => user.id === targetId || user.uid === targetId);

        targetUsername = targetUser?.username || "";
        targetEmail = targetUser?.email || "";
      }

      if (targetType === "auction") {
        const targetAuction = auctions.find((auction) => auction.id === targetId);

        targetTitle = targetAuction?.title || "";
        targetOwner = targetAuction?.createdByUsername || "";
        targetOwnerEmail = targetAuction?.createdBy || "";
      }

      if (targetType === "transaction") {
        const targetTransaction = transactions.find((transaction) => transaction.id === targetId);

        targetTransactionUser = getUserLabel(targetTransaction?.uid);
      }

      await addDoc(collection(db, "adminLogs"), {
        action,
        targetType,

        targetId: targetType !== "user" ? targetId : "",
        targetUid: targetType === "user" ? targetId : "",

        targetUsername,
        targetEmail,
        targetTitle,
        targetOwner,
        targetOwnerEmail,
        targetTransactionUser,
        details: details || {},
        adminUid: adminUser?.uid || "",
        adminEmail: adminUser?.email || "",
        createdAt: serverTimestamp(),
        createdAtText: new Date(now).toLocaleString(),
      });
    } catch (error) {
      console.error("Admin log error:", error);
    }
  };

  const handleCloseAuction = async (auctionId) => {
    const confirmClose = window.confirm("Are you sure you want to close this auction?");

    if (!confirmClose) return;

    try {
      const closedAtMs = Date.now();

      await updateDoc(doc(db, "auctions", auctionId), {
        status: "closed",
        live: false,
        closedAt: serverTimestamp(),
        closedAtMs,
        closureReason: "admin_closed",
        closedByAdmin: true,
        closedByAdminEmail: adminUser?.email || "",
      });

      setAuctions((prev) =>
        prev.map((auction) =>
          auction.id === auctionId
            ? {
                ...auction,
                status: "closed",
                live: false,
                closedAtMs,
                closureReason: "admin_closed",
                closedByAdmin: true,
                closedByAdminEmail: adminUser?.email || "",
              }
            : auction
        )
      );

      await writeAdminLog({
        action: "close_auction",
        targetType: "auction",
        targetId: auctionId,
        details: {
          status: "closed",
          live: false,
          closureReason: "admin_closed",
          closedAtMs,
        },
      });

      alert("Auction closed successfully.");
    } catch (error) {
      console.error("Close auction error:", error);
      alert("Could not close auction. Please try again.");
    }
  };

  const handleRemoveAuction = async (auctionId) => {
    const confirmRemove = window.confirm("Are you sure you want to REMOVE this auction?");

    if (!confirmRemove) return;

    try {
      console.log("Removing auction:", auctionId);

      await updateDoc(doc(db, "auctions", auctionId), {
        status: "removed",
        live: false,
        removedAt: Date.now(),
      });

      console.log("Firestore remove update succeeded");

      setAuctions((prev) =>
        prev.map((auction) =>
          auction.id === auctionId
            ? {
                ...auction,
                status: "removed",
                live: false,
                removedAt: Date.now(),
              }
            : auction
        )
      );

      await writeAdminLog({
        action: "remove_auction",
        targetType: "auction",
        targetId: auctionId,
        details: {
          status: "removed",
          live: false,
        },
      });

      alert("Auction removed successfully.");
    } catch (error) {
      console.error("Remove auction error:", error);
      alert("Could not remove auction.");
    }
  };

  const handleFlagAuction = async (auctionId) => {
    const flagReason = window.prompt("Enter the reason for flagging this auction:");

    if (!flagReason || !flagReason.trim()) return;

    try {
      await updateDoc(doc(db, "auctions", auctionId), {
        flagged: true,
        flagReason: flagReason.trim(),
        flaggedAt: Date.now(),
      });

      setAuctions((prev) =>
        prev.map((auction) =>
          auction.id === auctionId
            ? {
                ...auction,
                flagged: true,
                flagReason: flagReason.trim(),
                flaggedAt: Date.now(),
              }
            : auction
        )
      );

      await writeAdminLog({
        action: "flag_auction",
        targetType: "auction",
        targetId: auctionId,
        details: {
          flagged: true,
          flagReason: flagReason.trim(),
        },
      });

      alert("Auction flagged successfully.");
    } catch (error) {
      console.error("Flag auction error:", error);
      alert("Could not flag auction.");
    }
  };

  const handleUnflagAuction = async (auctionId) => {
    const confirmUnflag = window.confirm("Are you sure you want to unflag this auction?");

    if (!confirmUnflag) return;

    try {
      await updateDoc(doc(db, "auctions", auctionId), {
        flagged: false,
        flagReason: "",
        unflaggedAt: Date.now(),
      });

      setAuctions((prev) =>
        prev.map((auction) =>
          auction.id === auctionId
            ? {
                ...auction,
                flagged: false,
                flagReason: "",
                unflaggedAt: Date.now(),
              }
            : auction
        )
      );

      await writeAdminLog({
        action: "unflag_auction",
        targetType: "auction",
        targetId: auctionId,
        details: {
          flagged: false,
        },
      });

      alert("Auction unflagged successfully.");
    } catch (error) {
      console.error("Unflag auction error:", error);
      alert("Could not unflag auction.");
    }
  };


  const handleToggleDisableUser = async (userId, currentlyDisabled) => {
    const confirmToggle = window.confirm(
      currentlyDisabled
        ? "Are you sure you want to ENABLE this user?"
        : "Are you sure you want to DISABLE this user?"
    );

    if (!confirmToggle) return;

    try {
      await updateDoc(doc(db, "users", userId), {
        disabled: !currentlyDisabled,
        disabledAt: !currentlyDisabled ? Date.now() : null,
        enabledAt: currentlyDisabled ? Date.now() : null,
      });

      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,
                disabled: !currentlyDisabled,
                disabledAt: !currentlyDisabled ? Date.now() : null,
                enabledAt: currentlyDisabled ? Date.now() : null,
              }
            : user
        )
      );

      await writeAdminLog({
        action: currentlyDisabled ? "enable_user" : "disable_user",
        targetType: "user",
        targetId: userId,
        details: {
          disabled: !currentlyDisabled,
        },
      });

      alert(currentlyDisabled ? "User enabled successfully." : "User disabled successfully.");
    } catch (error) {
      console.error("Toggle disable user error:", error);
      alert("Could not update user status.");
    }
  };

  const handleToggleBanUser = async (userId, currentlyBanned) => {
    const confirmToggle = window.confirm(
      currentlyBanned
        ? "Are you sure you want to UNBAN this user?"
        : "Are you sure you want to BAN this user?"
    );

    if (!confirmToggle) return;

    try {
      await updateDoc(doc(db, "users", userId), {
        banned: !currentlyBanned,
        bannedAt: !currentlyBanned ? Date.now() : null,
        unbannedAt: currentlyBanned ? Date.now() : null,
      });

      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,
                banned: !currentlyBanned,
                bannedAt: !currentlyBanned ? Date.now() : null,
                unbannedAt: currentlyBanned ? Date.now() : null,
              }
            : user
        )
      );

      await writeAdminLog({
        action: currentlyBanned ? "unban_user" : "ban_user",
        targetType: "user",
        targetId: userId,
        details: {
          banned: !currentlyBanned,
        },
      });

      alert(currentlyBanned ? "User unbanned successfully." : "User banned successfully.");
    } catch (error) {
      console.error("Toggle ban user error:", error);
      alert("Could not update user ban status.");
    }
  };

  const handleArchiveUser = async (userId) => {
    const confirmArchive = window.confirm(
      "Are you sure you want to archive this user record?"
    );

    if (!confirmArchive) return;

    try {
      await updateDoc(doc(db, "users", userId), {
        archived: true,
        archivedAt: Date.now(),
      });

      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,
                archived: true,
                archivedAt: Date.now(),
              }
            : user
        )
      );

      setSelectedUserDetails(null);

      await writeAdminLog({
        action: "archive_user",
        targetType: "user",
        targetId: userId,
        details: {
          archived: true,
        },
      });

      await refreshAdminLogs();

      alert("User archived successfully.");
    } catch (error) {
      console.error("Archive user error:", error);
      alert("Could not archive user.");
    }
  };

  const handleUnarchiveUser = async (userId) => {
    const confirmUnarchive = window.confirm(
      "Are you sure you want to unarchive this user?"
    );

    if (!confirmUnarchive) return;

    try {
      await updateDoc(doc(db, "users", userId), {
        archived: false,
        unarchivedAt: Date.now(),
      });

      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,
                archived: false,
                unarchivedAt: Date.now(),
              }
            : user
        )
      );

      setSelectedUserDetails(null);

      await writeAdminLog({
        action: "unarchive_user",
        targetType: "user",
        targetId: userId,
        details: {
          archived: false,
        },
      });

      await refreshAdminLogs();

      alert("User unarchived successfully.");
    } catch (error) {
      console.error("Unarchive user error:", error);
      alert("Could not unarchive user.");
    }
  };

  const handleToggleAdminRole = async (userId, currentlyAdmin) => {
    const confirmAdminChange = window.confirm(
      currentlyAdmin
        ? "Are you sure you want to REMOVE admin role from this user?"
        : "Are you sure you want to mark this user as admin?"
    );

    if (!confirmAdminChange) return;

    try {
      await updateDoc(doc(db, "users", userId), {
        isAdmin: !currentlyAdmin,
        adminGrantedAt: !currentlyAdmin ? Date.now() : null,
        adminRemovedAt: currentlyAdmin ? Date.now() : null,
      });

      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,
                isAdmin: !currentlyAdmin,
                adminGrantedAt: !currentlyAdmin ? Date.now() : null,
                adminRemovedAt: currentlyAdmin ? Date.now() : null,
              }
            : user
        )
      );

      await writeAdminLog({
        action: currentlyAdmin ? "remove_admin_role" : "grant_admin_role",
        targetType: "user",
        targetId: userId,
        details: {
          isAdmin: !currentlyAdmin,
        },
      });

      alert(currentlyAdmin ? "Admin role removed successfully." : "User marked as admin successfully.");
    } catch (error) {
      console.error("Toggle admin role error:", error);
      alert("Could not update admin role.");
    }
  };

  const handleResetWallet =
  async (userId) => {
    const confirmReset =
      window.confirm(
        "Are you sure you want to RESET this user's wallet and credits?"
      );

    if (!confirmReset) {
      return;
    }

    try {
      const result =
        await runAdminWalletAdjustment({
          targetUid:
            userId,

          action:
            "reset_wallet",
        });

      const updatedUser =
        result.user ||
        {};

      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,

                walletBalanceKWD:
                  Number(
                    updatedUser
                      .walletBalanceKWD ||
                      0
                  ),

                paidWalletBalanceKWD:
                  Number(
                    updatedUser
                      .paidWalletBalanceKWD ||
                      0
                  ),

                freeWalletBalanceKWD:
                  Number(
                    updatedUser
                      .freeWalletBalanceKWD ||
                      0
                  ),

                credits:
                  updatedUser
                    .credits ||
                  {
                    "15": 0,
                    "30": 0,
                    "60": 0,
                    ext15: 0,
                  },

                walletLastUpdatedAt:
                  updatedUser
                    .walletLastUpdatedAt ||
                  Date.now(),
              }
            : user
        )
      );

      if (
        result.transaction
      ) {
        setTransactions(
          (prev) => [
            result.transaction,
            ...prev,
          ]
        );
      }

      await refreshAdminLogs();

      alert(
        "Wallet reset successfully."
      );
    } catch (error) {
      console.error(
        "Reset wallet error:",
        error
      );

      alert(
        error?.message ||
          "Could not reset wallet."
      );
    }
  };


  const handleAddPromotionalBalance =
  async (userId) => {
    const amountText =
      window.prompt(
        "Enter promotional balance amount in KWD:"
      );

    if (!amountText) {
      return;
    }

    const amount =
      Number(amountText);

    if (
      !amount ||
      amount <= 0
    ) {
      alert(
        "Please enter a valid amount greater than 0."
      );

      return;
    }

    const noteText =
      window.prompt(
        "Enter promotion note:",
        "Admin promotional credit"
      );

    try {
      const result =
        await runAdminWalletAdjustment({
          targetUid:
            userId,

          action:
            "add_promotional_balance",

          amountKWD:
            amount,

          note:
            noteText ||
            "Admin promotional credit",
        });

      const updatedUser =
        result.user ||
        {};

      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,

                walletBalanceKWD:
                  Number(
                    updatedUser
                      .walletBalanceKWD ||
                      0
                  ),

                paidWalletBalanceKWD:
                  Number(
                    updatedUser
                      .paidWalletBalanceKWD ||
                      0
                  ),

                freeWalletBalanceKWD:
                  Number(
                    updatedUser
                      .freeWalletBalanceKWD ||
                      0
                  ),

                credits:
                  updatedUser
                    .credits ||
                  user.credits,

                walletLastUpdatedAt:
                  updatedUser
                    .walletLastUpdatedAt ||
                  Date.now(),
              }
            : user
        )
      );

      if (
        result.transaction
      ) {
        setTransactions(
          (prev) => [
            result.transaction,
            ...prev,
          ]
        );
      }

      await refreshAdminLogs();

      await sendAdminNotificationToUser({
        uid:
          updatedUser.uid ||
          userId,

        title:
          "🎁 Wallet Credited",

        body:
          `Your wallet has been credited with ${Number(
            amount
          ).toFixed(
            3
          )} KWD. Reason: ${
            noteText ||
            "Admin promotional credit"
          }`,

        type:
          "wallet",

        notificationKey:
          `admin_wallet_credit_${userId}_${Date.now()}`,

        notificationType:
          "wallet_credited",

        notificationData: {
          amount:
            Number(
              amount ||
                0
            ),

          reason:
            noteText ||
            "",
        },
      });

      alert(
        "Promotional balance added successfully."
      );
    } catch (error) {
      console.error(
        "Add promotional balance error:",
        error
      );

      alert(
        error?.message ||
          "Could not add promotional balance."
      );
    }
  };


  const handleRefundAsCredit =
  async (userId) => {
    const creditType =
      window.prompt(
        "Enter credit type to refund: 15, 30, 60, or ext15"
      );

    if (!creditType) {
      return;
    }

    const allowedCredits = [
      "15",
      "30",
      "60",
      "ext15",
    ];

    if (
      !allowedCredits.includes(
        creditType
      )
    ) {
      alert(
        "Invalid credit type. Please enter only: 15, 30, 60, or ext15."
      );

      return;
    }

    const noteText =
      window.prompt(
        "Enter refund reason:",
        "Admin refund as time credit"
      );

    try {
      const result =
        await runAdminWalletAdjustment({
          targetUid:
            userId,

          action:
            "refund_credit",

          creditType,

          note:
            noteText ||
            "Admin refund as time credit",
        });

      const updatedUser =
        result.user ||
        {};

      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,

                credits:
                  updatedUser
                    .credits ||
                  user.credits,

                walletBalanceKWD:
                  Number(
                    updatedUser
                      .walletBalanceKWD ||
                      user
                        .walletBalanceKWD ||
                      0
                  ),

                paidWalletBalanceKWD:
                  Number(
                    updatedUser
                      .paidWalletBalanceKWD ||
                      user
                        .paidWalletBalanceKWD ||
                      0
                  ),

                freeWalletBalanceKWD:
                  Number(
                    updatedUser
                      .freeWalletBalanceKWD ||
                      user
                        .freeWalletBalanceKWD ||
                      0
                  ),

                walletLastUpdatedAt:
                  updatedUser
                    .walletLastUpdatedAt ||
                  Date.now(),
              }
            : user
        )
      );

      if (
        result.transaction
      ) {
        setTransactions(
          (prev) => [
            result.transaction,
            ...prev,
          ]
        );
      }

      await refreshAdminLogs();

      await sendAdminNotificationToUser({
        uid:
          updatedUser.uid ||
          userId,

        title:
          "↩️ Refund Credit Added",

        body:
          `A ${creditType} auction credit was added to your account. Reason: ${
            noteText ||
            "Admin refund as time credit"
          }`,

        type:
          "wallet",

        notificationKey:
          `admin_refund_credit_${userId}_${creditType}_${Date.now()}`,

        notificationType:
          "refund_credit_added",

        notificationData: {
          creditType:
            creditType ||
            "",

          reason:
            noteText ||
            "",
        },
      });

      alert(
        "Refund credit added successfully."
      );
    } catch (error) {
      console.error(
        "Refund as credit error:",
        error
      );

      alert(
        error?.message ||
          "Could not add refund credit."
      );
    }
  };


  const handleMarkTransactionManually = async (transactionId) => {
    const newStatus = window.prompt(
      "Enter new transaction status: succeeded, pending, failed, or reviewed"
    );

    if (!newStatus) return;

    const allowedStatuses = ["succeeded", "pending", "failed", "reviewed"];

    if (!allowedStatuses.includes(newStatus)) {
      alert("Invalid status. Please enter: succeeded, pending, failed, or reviewed.");
      return;
    }

    const noteText = window.prompt(
      "Enter admin note:",
      "Transaction status updated manually by admin"
    );

    try {
      await updateDoc(doc(db, "stg_walletTransactions", transactionId), {
        status: newStatus,
        adminNote: noteText || "Transaction status updated manually by admin",
        manuallyUpdatedAt: Date.now(),
      });

      setTransactions((prev) =>
        prev.map((transaction) =>
          transaction.id === transactionId
            ? {
                ...transaction,
                status: newStatus,
                adminNote: noteText || "Transaction status updated manually by admin",
                manuallyUpdatedAt: Date.now(),
              }
            : transaction
        )
      );

      await writeAdminLog({
        action: "mark_transaction_manually",
        targetType: "transaction",
        targetId: transactionId,
        details: {
          newStatus,
          adminNote: noteText || "Transaction status updated manually by admin",
        },
      });

      alert("Transaction updated successfully.");
    } catch (error) {
      console.error("Mark transaction manually error:", error);
      alert("Could not update transaction.");
    }
  };

  const handleAddPromotionalCredit =
  async (userId) => {
    const creditType =
      window.prompt(
        "Enter promotional credit type: 15, 30, 60, or ext15"
      );

    if (!creditType) {
      return;
    }

    const allowedCredits = [
      "15",
      "30",
      "60",
      "ext15",
    ];

    if (
      !allowedCredits.includes(
        creditType
      )
    ) {
      alert(
        "Invalid credit type. Please enter only: 15, 30, 60, or ext15."
      );

      return;
    }

    const noteText =
      window.prompt(
        "Enter promotion reason:",
        "Admin promotional time credit"
      );

    try {
      const result =
        await runAdminWalletAdjustment({
          targetUid:
            userId,

          action:
            "promotional_credit",

          creditType,

          note:
            noteText ||
            "Admin promotional time credit",
        });

      const updatedUser =
        result.user ||
        {};

      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,

                credits:
                  updatedUser
                    .credits ||
                  user.credits,

                walletBalanceKWD:
                  Number(
                    updatedUser
                      .walletBalanceKWD ||
                      user
                        .walletBalanceKWD ||
                      0
                  ),

                paidWalletBalanceKWD:
                  Number(
                    updatedUser
                      .paidWalletBalanceKWD ||
                      user
                        .paidWalletBalanceKWD ||
                      0
                  ),

                freeWalletBalanceKWD:
                  Number(
                    updatedUser
                      .freeWalletBalanceKWD ||
                      user
                        .freeWalletBalanceKWD ||
                      0
                  ),

                walletLastUpdatedAt:
                  updatedUser
                    .walletLastUpdatedAt ||
                  Date.now(),
              }
            : user
        )
      );

      if (
        result.transaction
      ) {
        setTransactions(
          (prev) => [
            result.transaction,
            ...prev,
          ]
        );
      }

      await refreshAdminLogs();

      await sendAdminNotificationToUser({
        uid:
          updatedUser.uid ||
          userId,

        title:
          "🎟️ Promo Credit Added",

        body:
          `A ${creditType} auction credit was added to your account. Reason: ${
            noteText ||
            "Admin promotional time credit"
          }`,

        type:
          "wallet",

        notificationKey:
          `admin_promo_credit_${userId}_${creditType}_${Date.now()}`,

        notificationType:
          "promo_credit_added",

        notificationData: {
          creditType:
            creditType ||
            "",

          reason:
            noteText ||
            "",
        },
      });

      alert(
        "Promotional credit added successfully."
      );
    } catch (error) {
      console.error(
        "Add promotional credit error:",
        error
      );

      alert(
        error?.message ||
          "Could not add promotional credit."
      );
    }
  };


  const handleViewUserDetails = async (user) => {
    if (!user?.id) {
      return;
    }

    setSelectedUserDetails(user);
    setSelectedUserRatings([]);
    setSelectedUserRatingsLoading(true);

    try {
      const ratingsSnapshot = await getDocs(
        collection(
          db,
          "users",
          user.id,
          "ratings"
        )
      );

      const ratingRows = ratingsSnapshot.docs
        .map((ratingDoc) => ({
          id: ratingDoc.id,
          ...ratingDoc.data(),
        }))
        .sort((a, b) => {
          const aTime =
            a.createdAt?.seconds
              ? a.createdAt.seconds * 1000
              : Number(
                  a.createdAtMs ||
                    a.timestamp ||
                    0
                );

          const bTime =
            b.createdAt?.seconds
              ? b.createdAt.seconds * 1000
              : Number(
                  b.createdAtMs ||
                    b.timestamp ||
                    0
                );

          return bTime - aTime;
        });

      setSelectedUserRatings(
        ratingRows
      );
    } catch (error) {
      console.error(
        "Load seller ratings error:",
        error
      );

      setSelectedUserRatings([]);
    } finally {
      setSelectedUserRatingsLoading(
        false
      );
    }
  };

  const handleViewAuctionDetails = async (auction) => {
    setSelectedAuctionDetails(auction);
    setAuctionRecordingDetails(null);
    setAuctionRecordingSessions([]);
  
    try {
      const recordingsSnapshot = await getDocs(
        collection(db, "auctions", auction.id, "recordings")
      );

      const recordingDocs = recordingsSnapshot.docs.map((recordingDoc) => ({
        id: recordingDoc.id,
        ...recordingDoc.data(),
      }));

      const currentRecording = recordingDocs.find(
        (recording) => recording.id === "_current"
      );

      const sessionRecordings = recordingDocs
        .filter((recording) => recording.id !== "_current")
        .sort((a, b) => {
          const aTime = a.startedAt?.seconds
            ? a.startedAt.seconds * 1000
            : Number(a.startedAt || 0);

          const bTime = b.startedAt?.seconds
            ? b.startedAt.seconds * 1000
            : Number(b.startedAt || 0);

          return aTime - bTime;
        });

      if (currentRecording) {
        setAuctionRecordingDetails(currentRecording);
      }

      setAuctionRecordingSessions(sessionRecordings);
    } catch (error) {
      console.error("Load auction recording details error:", error);
    }
  };
  
  const handleAddAuctionAdminNote = async () => {
    if (!selectedAuctionDetails?.id) return;
  
    const noteText = window.prompt("Write an admin note for this auction:");
  
    if (!noteText || !noteText.trim()) return;
  
    const newNote = {
      note: noteText.trim(),
      adminEmail: adminUser?.email || "",
      createdAt: Date.now(),
    };
  
    const updatedNotes = [
      ...(selectedAuctionDetails.adminNotes || []),
      newNote,
    ];
  
    try {
      await updateDoc(doc(db, "auctions", selectedAuctionDetails.id), {
        adminNotes: updatedNotes,
        lastAdminNote: noteText.trim(),
        lastAdminNoteAt: Date.now(),
      });
  
      setSelectedAuctionDetails((prev) => ({
        ...prev,
        adminNotes: updatedNotes,
        lastAdminNote: noteText.trim(),
        lastAdminNoteAt: Date.now(),
      }));
  
      setAuctions((prev) =>
        prev.map((auction) =>
          auction.id === selectedAuctionDetails.id
            ? {
                ...auction,
                adminNotes: updatedNotes,
                lastAdminNote: noteText.trim(),
                lastAdminNoteAt: Date.now(),
              }
            : auction
        )
      );
  
      alert("Admin note added successfully.");
    } catch (error) {
      console.error("Add auction admin note error:", error);
      alert("Could not add admin note.");
    }
  };
  
  const handleAddUserAdminNote = async () => {
    if (!selectedUserDetails?.id) return;
  
    const noteText = window.prompt("Write an admin note for this user:");
  
    if (!noteText || !noteText.trim()) return;
  
    const newNote = {
      note: noteText.trim(),
      adminEmail: adminUser?.email || "",
      createdAt: Date.now(),
    };
  
    const updatedNotes = [
      ...(selectedUserDetails.adminNotes || []),
      newNote,
    ];
  
    try {
      await updateDoc(doc(db, "users", selectedUserDetails.id), {
        adminNotes: updatedNotes,
        lastAdminNote: noteText.trim(),
        lastAdminNoteAt: Date.now(),
      });
  
      setSelectedUserDetails((prev) => ({
        ...prev,
        adminNotes: updatedNotes,
        lastAdminNote: noteText.trim(),
        lastAdminNoteAt: Date.now(),
      }));
  
      setUsers((prev) =>
        prev.map((user) =>
          user.id === selectedUserDetails.id
            ? {
                ...user,
                adminNotes: updatedNotes,
                lastAdminNote: noteText.trim(),
                lastAdminNoteAt: Date.now(),
              }
            : user
        )
      );
  
      alert("Admin note added successfully.");
    } catch (error) {
      console.error("Add user admin note error:", error);
      alert("Could not add admin note.");
    }
  };
  
  const handleOpenRecording = async (recordingOverride = null) => {
    const recordingSource = recordingOverride || auctionRecordingDetails;

    const recordingFileList =
      recordingSource?.stopResponse?.serverResponse?.fileList ||
      recordingSource?.stopResponse?.fileList ||
      recordingSource?.fileList ||
      [];
  
    const firstRecordingFile =
      Array.isArray(recordingFileList) && recordingFileList.length > 0
        ? recordingFileList.find((file) =>
            String(file.fileName || file.filename || file)
              .toLowerCase()
              .includes(".mp4")
          ) || recordingFileList[0]
        : null;
  
    const recordingFileName =
      firstRecordingFile?.fileName ||
      firstRecordingFile?.filename ||
      firstRecordingFile?.name ||
      (typeof firstRecordingFile === "string" ? firstRecordingFile : "");
  
      const oldRecordingPath =
      recordingSource?.storagePaths?.find((path) =>
        String(path || "").toLowerCase().includes(".mp4")
      ) ||
      recordingSource?.storagePaths?.[0] ||
      selectedAuctionDetails?.storagePaths?.[0] ||
      selectedAuctionDetails?.lastRecording?.paths?.[0] ||
      "";
  
    const baseRecordingFolder = oldRecordingPath
      ? oldRecordingPath.substring(0, oldRecordingPath.lastIndexOf("/") + 1)
      : "";
  
    const recordingPath =
      recordingFileName && recordingFileName.includes("/")
        ? recordingFileName
        : recordingFileName && baseRecordingFolder
        ? `${baseRecordingFolder}${recordingFileName}`
        : oldRecordingPath;
  
    if (!recordingPath) {
      alert("No recording file found for this auction yet.");
      return;
    }
  
    try {
      setRecordingLoading(true);
  
      const currentUser = auth.currentUser;
  
      if (!currentUser) {
        alert("Admin session expired. Please sign in again.");
        return;
      }
  
      const idToken = await currentUser.getIdToken();
  
      const response = await fetch(
        `https://us-central1-q8auctionapp-2c6dd.cloudfunctions.net/getRecordingSignedUrl?path=${encodeURIComponent(
          recordingPath
        )}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
  
      const data = await response.json();
  
      if (!response.ok || !data.url) {
        throw new Error(data.error || "Could not create signed recording URL.");
      }
  
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Open recording error:", error);
  
      alert(
        `Could not open the recording.\n\nPath:\n${recordingPath}\n\nError:\n${
          error.message || "Unknown error"
        }`
      );
    } finally {
      setRecordingLoading(false);
    }
  };
  
if (!isAdminEntry) {
  return (
    <div className="login-page">
      <div className="login-card">
        <h1>X-AUCTIONS</h1>

        <p>
          Welcome to X-AUCTIONS.
        </p>
      </div>
    </div>
  );
}

  if (authLoading) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>X-AUCTIONS Admin</h1>
          <p>Checking admin access...</p>
        </div>
      </div>
    );
  }
  
  if (mfaEnrollmentRequired) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>X-AUCTIONS Admin</h1>
  
          <p>
            Protect this web-admin account with SMS two-factor authentication.
          </p>
  
          {!mfaEnrollmentCodeSent ? (
            <form onSubmit={handleSendEnrollmentSms}>
              {mfaEnrollmentError ? (
                <div className="login-error">
                  {mfaEnrollmentError}
                </div>
              ) : null}
  
              <input
                className="login-input"
                placeholder="+965XXXXXXXX"
                type="tel"
                value={mfaPhoneNumber}
                onChange={(e) =>
                  setMfaPhoneNumber(e.target.value)
                }
              />
  
              <div
                id="admin-mfa-recaptcha"
                style={{
                  marginTop: 14,
                  marginBottom: 14,
                }}
              />
  
              <button
                className="login-button"
                type="submit"
                disabled={mfaEnrollmentLoading}
              >
                {mfaEnrollmentLoading
                  ? "Sending SMS..."
                  : "Send SMS Code"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSmsEnrollment}>
              {mfaEnrollmentError ? (
                <div className="login-error">
                  {mfaEnrollmentError}
                </div>
              ) : null}
  
              <p
                style={{
                  fontSize: 14,
                  lineHeight: 1.5,
                  marginTop: 12,
                  marginBottom: 12,
                }}
              >
                Enter the 6-digit code sent to your phone.
              </p>
  
              <input
                className="login-input"
                placeholder="6-digit SMS code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={mfaEnrollmentCode}
                onChange={(e) =>
                  setMfaEnrollmentCode(
                    e.target.value.replace(/\D/g, "")
                  )
                }
              />
  
              <button
                className="login-button"
                type="submit"
                disabled={mfaEnrollmentLoading}
              >
                {mfaEnrollmentLoading
                  ? "Enabling..."
                  : "Enable SMS Two-Factor"}
              </button>
  
              <button
                type="button"
                className="filter-btn"
                style={{
                  marginTop: 10,
                  width: "100%",
                }}
                onClick={() => {
                  setMfaEnrollmentCodeSent(false);
                  setMfaEnrollmentVerificationId("");
                  setMfaEnrollmentCode("");
                  setMfaEnrollmentError("");
                }}
              >
                Use Different Phone Number
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }
  
  if (mfaResolver) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>X-AUCTIONS Admin</h1>
  
          <p>Two-factor authentication required.</p>
  
          {!mfaLoginVerificationId ? (
            <>
              {loginError ? (
                <div className="login-error">
                  {loginError}
                </div>
              ) : null}
  
              <div
                id="admin-mfa-login-recaptcha"
                style={{
                  marginTop: 14,
                  marginBottom: 14,
                }}
              />
  
              <button
                className="login-button"
                type="button"
                onClick={handleSendMfaLoginSms}
              >
                Send SMS Code
              </button>
  
              <button
                type="button"
                className="filter-btn"
                style={{
                  marginTop: 10,
                  width: "100%",
                }}
                onClick={async () => {
                  setMfaResolver(null);
                  setMfaLoginCode("");
                  setMfaLoginVerificationId("");
                  setLoginError("");
                  setLoginPassword("");
  
                  await signOut(auth).catch(() => {});
                }}
              >
                Back to Sign In
              </button>
            </>
          ) : (
            <form onSubmit={handleSmsMfaLogin}>
              {loginError ? (
                <div className="login-error">
                  {loginError}
                </div>
              ) : null}
  
              <p
                style={{
                  fontSize: 14,
                  lineHeight: 1.5,
                  marginTop: 12,
                  marginBottom: 12,
                }}
              >
                Enter the 6-digit code sent to your phone.
              </p>
  
              <input
                className="login-input"
                placeholder="6-digit SMS code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={mfaLoginCode}
                onChange={(e) =>
                  setMfaLoginCode(
                    e.target.value.replace(/\D/g, "")
                  )
                }
                autoFocus
              />
  
              <button
                className="login-button"
                type="submit"
              >
                Verify & Sign In
              </button>
  
              <button
                type="button"
                className="filter-btn"
                style={{
                  marginTop: 10,
                  width: "100%",
                }}
                onClick={() => {
                  setMfaLoginVerificationId("");
                  setMfaLoginCode("");
                  setLoginError("");
                }}
              >
                Send Another Code
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }
  
  if (!adminUser) {
    return (
      <div className="login-page">
        <form
          className="login-card"
          onSubmit={handleAdminLogin}
        >
          <h1>X-AUCTIONS Admin</h1>
  
          <p>
            Sign in with the authorized web-admin account.
          </p>
  
          {loginError ? (
            <div className="login-error">
              {loginError}
            </div>
          ) : null}
  
          <input
            className="login-input"
            placeholder="Admin email"
            type="email"
            autoComplete="username"
            value={loginEmail}
            onChange={(e) =>
              setLoginEmail(e.target.value)
            }
          />
  
          <input
            className="login-input"
            placeholder="Password"
            type="password"
            autoComplete="current-password"
            value={loginPassword}
            onChange={(e) =>
              setLoginPassword(e.target.value)
            }
          />
  
          <button
            className="login-button"
            type="submit"
          >
            Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-page">
          <header className="admin-header">
  <div>
    <h1>X-AUCTIONS Admin Panel</h1>
    <p>Owner dashboard for users, auctions, payments, and platform activity.</p>
  </div>

  <button className="logout-button" onClick={handleAdminLogout}>
    Sign Out
  </button>
</header>



      <nav className="admin-tabs">
  <button
    className={activeSection === "dashboard" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("dashboard")}
  >
    Dashboard
  </button>

  <button
    className={activeSection === "users" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("users")}
  >
    Users
  </button>

  <button
    className={activeSection === "auctions" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("auctions")}
  >
    Auctions
  </button>

  <button
    className={activeSection === "payments" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("payments")}
  >
    Payments
  </button>

  <button
    className={activeSection === "logs" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("logs")}
  >
    Admin Logs
  </button>

  <button
    className={activeSection === "notifications" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("notifications")}
  >
    Notifications
  </button>

  <button
    className={activeSection === "categories" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("categories")}
  >
    Categories
  </button>

  <button
    className={activeSection === "analytics" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("analytics")}
  >
    Analytics
  </button>
  <button
    className={activeSection === "sellerAnalytics" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("sellerAnalytics")}
  >
    Seller Analytics
  </button>
  <button
    className={activeSection === "buyerRetention" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("buyerRetention")}
  >
    Buyer Retention
  </button>
  <button
    className={activeSection === "conversionMetrics" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("conversionMetrics")}
  >
    Conversion Metrics
  </button>
  <button
    className={activeSection === "fraudRisk" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("fraudRisk")}
  >
    Fraud Risk
  </button>
  <button
    className={activeSection === "suspiciousBidding" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("suspiciousBidding")}
  >
    Suspicious Bidding
  </button>
  <button
    className={activeSection === "deviceMonitoring" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("deviceMonitoring")}
  >
    Device Monitoring
  </button>
  <button
    className={activeSection === "auctionPerformance" ? "admin-tab active" : "admin-tab"}
    onClick={() => setActiveSection("auctionPerformance")}
  >
    Auction Performance
  </button>
  </nav>

<section className="table-card">
  <div className="table-header">
    <div>
      <h2>Global Date Range</h2>
      <p>Temporarily filter dashboard and tables by date. Default is All Time after refresh.</p>
    </div>

    <div className="filter-buttons">
      <button
        className={globalDateRange === "all" ? "filter-btn active" : "filter-btn"}
        onClick={() => setGlobalDateRange("all")}
      >
        All Time
      </button>

      <button
        className={globalDateRange === "today" ? "filter-btn active" : "filter-btn"}
        onClick={() => setGlobalDateRange("today")}
      >
        Today
      </button>

      <button
        className={globalDateRange === "last7" ? "filter-btn active" : "filter-btn"}
        onClick={() => setGlobalDateRange("last7")}
      >
        Last 7 Days
      </button>

      <button
        className={globalDateRange === "last30" ? "filter-btn active" : "filter-btn"}
        onClick={() => setGlobalDateRange("last30")}
      >
        Last 30 Days
      </button>

      <button
        className={globalDateRange === "thisMonth" ? "filter-btn active" : "filter-btn"}
        onClick={() => setGlobalDateRange("thisMonth")}
      >
        This Month
      </button>
    </div>
  </div>
</section>
{activeSection === "notifications" && (
  <NotificationCenterPage auctions={auctions} users={users} />
)}

{activeSection === "categories" && (
  <CategoryManagementPage />
)}

{activeSection === "analytics" && (
  <AnalyticsPage
    users={users}
    auctions={auctions}
    transactions={transactions}
    dateFilteredAuctions={dateFilteredAuctions}
    dateFilteredTransactions={dateFilteredTransactions}
    getTransactionAllocation={getTransactionAllocation}
  />
)}

{activeSection === "sellerAnalytics" && (
  <SellerAnalyticsPage
  users={users}
  auctions={auctions}
  dateFilteredAuctions={dateFilteredAuctions}
  dateFilteredTransactions={dateFilteredTransactions}
  getTransactionAllocation={getTransactionAllocation}
/>
)}

{activeSection === "buyerRetention" && (
  <BuyerRetentionPage
    users={users}
    dateFilteredAuctions={dateFilteredAuctions}
  />
)}

{activeSection === "conversionMetrics" && (
  <ConversionMetricsPage
    users={users}
    auctions={auctions}
    dateFilteredAuctions={dateFilteredAuctions}
    dateFilteredTransactions={dateFilteredTransactions}
  />
)}

{activeSection === "fraudRisk" && (
  <FraudRiskPage
    users={users}
    auctions={auctions}
    transactions={transactions}
    adminLogs={adminLogs}
    dateFilteredAuctions={dateFilteredAuctions}
    dateFilteredTransactions={dateFilteredTransactions}
    onViewUser={(riskUser) => {
      const matchedUser = users.find(
        (user) =>
          user.uid === riskUser.uid ||
          user.id === riskUser.uid ||
          user.email === riskUser.email ||
          user.username === riskUser.username
      );

      if (!matchedUser) {
        alert("Could not find this user record.");
        return;
      }

      setSelectedUserDetails(matchedUser);
      setActiveSection("users");
    }}
    onViewRelatedAuctions={(riskUser) => {
      setAuctionSearch(riskUser.email || riskUser.username || "");
      setAuctionStatusFilter("all");
      setActiveSection("auctions");
    }}
  />
)}

{activeSection === "suspiciousBidding" && (
  <SuspiciousBiddingPage
    users={users}
    dateFilteredAuctions={dateFilteredAuctions}
  />
)}

{activeSection === "deviceMonitoring" && (
  <DeviceMonitoringPage
    users={users}
    dateFilteredAuctions={dateFilteredAuctions}
  />
)}

{activeSection === "auctionPerformance" && (
  <AuctionPerformancePage
    dateFilteredAuctions={dateFilteredAuctions}
  />
)}

<section className={activeSection === "dashboard" ? "stats-grid" : "hidden-section"}>
      <div className="stat-card">
          <span>Active Users</span>
          <strong>{users.filter((user) => user.archived !== true).length}</strong>
        </div>

        <div className="stat-card">
          <span>Archived Users</span>
          <strong>{users.filter((user) => user.archived === true).length}</strong>
        </div>

        <div className="stat-card">
          <span>Users With Credits</span>

          <strong>
            {
              users.filter(
                (user) =>
                  Number(user.credits?.["15"] || 0) +
                    Number(user.credits?.["30"] || 0) +
                    Number(user.credits?.["60"] || 0) +
                    Number(user.credits?.ext15 || 0) >
                  0
              ).length
            }
          </strong>
        </div>

        <div className="stat-card">
          <span>Total Credits Outstanding</span>

          <small style={{ display: "block", marginTop: 6 }}>
            15m:{" "}
            {users.reduce(
              (sum, user) =>
                sum + Number(user.credits?.["15"] || 0),
              0
            )}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            30m:{" "}
            {users.reduce(
              (sum, user) =>
                sum + Number(user.credits?.["30"] || 0),
              0
            )}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            60m:{" "}
            {users.reduce(
              (sum, user) =>
                sum + Number(user.credits?.["60"] || 0),
              0
            )}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            ext15:{" "}
            {users.reduce(
              (sum, user) =>
                sum + Number(user.credits?.ext15 || 0),
              0
            )}
          </small>
        </div>

        <div className="stat-card">
          <span>Negative Wallet Users</span>

          <strong>
            {
              users.filter(
                (user) =>
                  Number(user.walletBalanceKWD || 0) < 0
              ).length
            }
          </strong>
        </div>

        <div className="stat-card">
          <span>Total User Records</span>
          <strong>{users.length}</strong>
        </div>

        <div className="stat-card">
          <span>Phone Verified</span>
          <strong>{users.filter((user) => user.phoneVerified === true).length}</strong>
        </div>

        <div className="stat-card">
          <span>Banned Users</span>
          <strong>{users.filter((user) => user.banned === true).length}</strong>
        </div>

        <div className="stat-card">
          <span>Disabled Users</span>
          <strong>{users.filter((user) => user.disabled === true).length}</strong>
        </div>

        <div className="stat-card">
          <span>Admins</span>
          <strong>{users.filter((user) => user.isAdmin === true).length}</strong>
        </div>

        <div className="stat-card">
          <span>Newest User</span>

          <strong>
            {newestUser
              ? newestUser.username || "Unnamed User"
              : "—"}
          </strong>

          <small style={{ display: "block", marginTop: 4 }}>
            {newestUser
              ? newestUser.email || "—"
              : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            Joined:{" "}
            {newestUser
              ? newestUser.createdAt?.seconds
                ? new Date(
                    newestUser.createdAt.seconds * 1000
                  ).toLocaleString()
                : newestUser.joinedAt
                ? new Date(
                    Number(newestUser.joinedAt)
                  ).toLocaleString()
                : "Unknown"
              : "—"}
          </small>
        </div>

        <div className="stat-card">
          <span>Total Auctions</span>
          <strong>{dateFilteredAuctions.length}</strong>
        </div>

        <div className="stat-card">
          <span>Open Auctions</span>
          <strong>{dateFilteredAuctions.filter((auction) => auction.status === "open").length}</strong>
        </div>

        <div className="stat-card">
          <span>Scheduled Auctions</span>
          <strong>{dateFilteredAuctions.filter((auction) => auction.status === "scheduled").length}</strong>
        </div>

        <div className="stat-card">
          <span>Live Auctions</span>
          <strong>{dateFilteredAuctions.filter((auction) => auction.live === true).length}</strong>
        </div>

        <div className="stat-card">
          <span>Flagged Auctions</span>
          <strong>{dateFilteredAuctions.filter((auction) => auction.flagged === true).length}</strong>
        </div>

        <div className="stat-card">
          <span>Reported Auctions</span>

          <strong>
  {
    dateFilteredAuctions.filter(
      (auction) =>
        auction.reported === true ||
        Number(auction.reportCount || 0) > 0
    ).length
  }
</strong>
        </div>

        <div className="stat-card">
          <span>Most Reported Auction</span>

          <strong>
            {mostReportedAuction
              ? mostReportedAuction.title || "Untitled Auction"
              : "—"}
          </strong>

          <small style={{ display: "block", marginTop: 4 }}>
            Seller:{" "}
            {mostReportedAuction
              ? mostReportedAuction.createdByUsername ||
                mostReportedAuction.createdBy ||
                "—"
              : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            Reports:{" "}
            {mostReportedAuction
              ? Number(mostReportedAuction.reportCount || 0)
              : 0}
          </small>
        </div>

        <div className="stat-card">
          <span>Removed Auctions</span>
          <strong>{dateFilteredAuctions.filter((auction) => auction.status === "removed").length}</strong>
        </div>

        <div className="stat-card">
          <span>Closed Auctions</span>
          <strong>{dateFilteredAuctions.filter((auction) => auction.status === "closed").length}</strong>
        </div>

        <div className="stat-card">
          <span>Top Bid Auction</span>

          <strong>
            {topBidAuction
              ? topBidAuction.title || "Untitled Auction"
              : "—"}
          </strong>

          <small style={{ display: "block", marginTop: 4 }}>
            Seller:{" "}
            {topBidAuction
              ? topBidAuction.createdByUsername ||
                topBidAuction.createdBy ||
                "—"
              : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            Current Bid:{" "}
            {topBidAuction
              ? `${Number(topBidAuction.currentBid || 0).toFixed(3)} KWD`
              : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            Winner / Top Bidder:{" "}
            {topBidAuction
              ? topBidAuction.currentWinner ||
                topBidAuction.winner ||
                "—"
              : "—"}
          </small>
        </div>

        <div className="stat-card">
          <span>Highest Bid Ever</span>

          <strong>
            {highestBidEverAuction
              ? `${Number(highestBidEverAuction.currentBid || 0).toFixed(3)} KWD`
              : "0.000 KWD"}
          </strong>

          <small style={{ display: "block", marginTop: 4 }}>
            Auction:{" "}
            {highestBidEverAuction
              ? highestBidEverAuction.title || "Untitled Auction"
              : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            Winner / Top Bidder:{" "}
            {highestBidEverAuction
              ? highestBidEverAuction.currentWinner ||
                highestBidEverAuction.winner ||
                "—"
              : "—"}
          </small>
        </div>

        <div className="stat-card">
          <span>Newest Auction</span>

          <strong>
            {newestAuction
              ? newestAuction.title || "Untitled Auction"
              : "—"}
          </strong>

          <small style={{ display: "block", marginTop: 4 }}>
            Seller:{" "}
            {newestAuction
              ? newestAuction.createdByUsername ||
                newestAuction.createdBy ||
                "—"
              : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            Status:{" "}
            {newestAuction
              ? newestAuction.status || "—"
              : "—"}
          </small>
          <small style={{ display: "block", marginTop: 4 }}>
            Created:{" "}
            {newestAuction?.createdAt?.seconds
              ? new Date(
                  newestAuction.createdAt.seconds * 1000
                ).toLocaleString()
              : newestAuction?.createdAt
              ? new Date(
                  Number(newestAuction.createdAt)
                ).toLocaleString()
              : "Unknown"}
          </small>
        </div>

        <div className="stat-card">
          <span>Total Wallet Balance</span>
          <strong>{totalWalletBalance.toFixed(3)} KWD</strong>
        </div>

        <div className="stat-card">
          <span>Average Wallet Per User</span>
          <strong>{averageWalletPerUser.toFixed(3)} KWD</strong>
        </div>

        <div className="stat-card">
          <span>Top Wallet User</span>

          <strong>
            {topWalletUser
              ? topWalletUser.username || "Unnamed User"
              : "—"}
          </strong>

          <small style={{ display: "block", marginTop: 4 }}>
            {topWalletUser
              ? topWalletUser.email || "—"
              : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            Balance:{" "}
            {topWalletUser
              ? `${Number(topWalletUser.walletBalanceKWD || 0).toFixed(3)} KWD`
              : "—"}
          </small>
        </div>

        <div className="stat-card">
          <span>Top Credit Holder</span>

          <strong>
            {topCreditHolder
              ? topCreditHolder.username || "Unnamed User"
              : "—"}
          </strong>

          <small style={{ display: "block", marginTop: 4 }}>
            {topCreditHolder
              ? topCreditHolder.email || "—"
              : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            15: {Number(topCreditHolder?.credits?.["15"] || 0)}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            30: {Number(topCreditHolder?.credits?.["30"] || 0)}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            60: {Number(topCreditHolder?.credits?.["60"] || 0)}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            ext15: {Number(topCreditHolder?.credits?.ext15 || 0)}
          </small>
        </div>

        <div className="stat-card">
          <span>Most Flagged Seller</span>

          <strong>
            {mostFlaggedSeller
              ? mostFlaggedSeller.username || "Unnamed Seller"
              : mostFlaggedSellerEntry
              ? mostFlaggedSellerEntry[0]
              : "—"}
          </strong>

          <small style={{ display: "block", marginTop: 4 }}>
            {mostFlaggedSeller
              ? mostFlaggedSeller.email || "—"
              : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            Flagged Auctions:{" "}
            {mostFlaggedSellerEntry
              ? mostFlaggedSellerEntry[1]
              : 0}
          </small>
        </div>

        

        <div className="stat-card">
          <span>Most Active Seller</span>

          <strong>
            {mostActiveSeller
              ? mostActiveSeller.username || "Unnamed Seller"
              : mostActiveSellerEntry
              ? mostActiveSellerEntry[0]
              : "—"}
          </strong>

          <small style={{ display: "block", marginTop: 4 }}>
            {mostActiveSeller
              ? mostActiveSeller.email || "—"
              : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            Auctions:{" "}
            {mostActiveSellerEntry
              ? mostActiveSellerEntry[1]
              : 0}
          </small>
        </div>

        <div className="stat-card">
          <span>Highest Rated Seller</span>

          <strong>
            {highestRatedSeller
              ? highestRatedSeller.username || "Unnamed Seller"
              : "—"}
          </strong>

          <small style={{ display: "block", marginTop: 4 }}>
            {highestRatedSeller
              ? highestRatedSeller.email || "—"
              : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            Rating:{" "}
            {highestRatedSeller
              ? `${Number(highestRatedSeller.averageRating || 0).toFixed(1)} ⭐`
              : "—"}
          </small>
        </div>

        <div className="stat-card">
  <span>Total Paid In</span>

  <strong>
    {Number(totalPaidValue || 0).toFixed(3)} KWD
  </strong>

  <small style={{ display: "block", marginTop: 4 }}>
    Real money received from successful top-ups
  </small>
</div>

<div className="stat-card">
  <span>Total Real Revenue</span>

  <strong>
    {Number(totalRevenue || 0).toFixed(3)} KWD
  </strong>

  <small style={{ display: "block", marginTop: 4 }}>
    Paid wallet value already consumed
  </small>
</div>

        <div className="stat-card">
  <span>Paid Wallet Liability</span>

  <strong>
    {Number(totalPaidWalletLiability || 0).toFixed(3)} KWD
  </strong>

  <small style={{ display: "block", marginTop: 4 }}>
    Real paid wallet balance still owed to users
  </small>
</div>

<div className="stat-card">
  <span>Paid Revenue Reconciliation Difference</span>

  <strong>
    {Number(paidRevenueReconciliationDifference || 0).toFixed(3)} KWD
  </strong>

  <small style={{ display: "block", marginTop: 4 }}>
    Expected 0.000 KWD. Non-zero means review old payments, revenue, or paid wallet balance.
  </small>
</div>

<div className="stat-card">
  <span>Free Wallet Liability</span>

  <strong>
    {Number(totalFreeWalletLiability || 0).toFixed(3)} KWD
  </strong>

  <small style={{ display: "block", marginTop: 4 }}>
    Admin/free wallet balance still unused
  </small>
</div>

<div className="stat-card">
  <span>Free Grant Reconciliation Difference</span>

  <strong>
    {Number(freeGrantReconciliationDifference || 0).toFixed(3)} KWD
  </strong>

  <small style={{ display: "block", marginTop: 4 }}>
    Expected 0.000 KWD. Non-zero means free grant accounting mismatch.
  </small>
</div>

<div className="stat-card">
  <span>Total Free Grants Given</span>

  <strong>
    {Number(totalFreeGrantsGiven || 0).toFixed(3)} KWD
  </strong>

  <small style={{ display: "block", marginTop: 4 }}>
    Promotional/admin wallet value granted to users
  </small>
</div>

        <div className="stat-card">
        <span>Free Grant Consumed</span>

          <strong>
            {Number(freeValueConsumed || 0).toFixed(3)} KWD
          </strong>

          <small style={{ display: "block", marginTop: 4 }}>
          Admin/free value already used
          </small>
        </div>

        <div className="stat-card">
          <span>Highest Revenue User</span>

          <strong>
            {topPayingUsers.length > 0
              ? topPayingUsers[0].username || "Unnamed User"
              : "—"}
          </strong>

          <small style={{ display: "block", marginTop: 4 }}>
            {topPayingUsers.length > 0
              ? topPayingUsers[0].email || "—"
              : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            Revenue:{" "}
            {topPayingUsers.length > 0
                                          ? `${Number(topPayingUsers[0].revenueGenerated || 0).toFixed(3)} KWD`
              : "0.000 KWD"}
          </small>
        </div>

        <div className="stat-card">
        <span>Net Transaction Movement</span>
          <strong>{totalTransactionValue.toFixed(3)} KWD</strong>
        </div>

        <div className="stat-card">
          <span>Transactions</span>
          <strong>{dateFilteredTransactions.length}</strong>
        </div>

        <div className="stat-card">
          <span>Pending Transactions</span>
          <strong>{dateFilteredTransactions.filter((transaction) => transaction.status === "pending").length}</strong>
        </div>

        <div className="stat-card">
          <span>Failed Transactions</span>
          <strong>{dateFilteredTransactions.filter((transaction) => transaction.status === "failed").length}</strong>
        </div>

        <div className="stat-card">
          <span>Succeeded Transactions</span>
          <strong>{dateFilteredTransactions.filter((transaction) => transaction.status === "succeeded").length}</strong>
        </div>

        <div className="stat-card">
          <span>Admin Actions</span>
          <strong>{dateFilteredAdminLogs.length}</strong>
        </div>

        <div className="stat-card">
          <span>Latest Admin Action</span>

          <strong>
            {latestAdminLog
              ? formatAdminActionLabel(latestAdminLog.action)
              : "—"}
          </strong>

          <small style={{ display: "block", marginTop: 4 }}>
            Target:{" "}
            {latestAdminLog
              ? latestAdminLog.targetEmail ||
                latestAdminLog.targetOwnerEmail ||
                latestAdminLog.targetUsername ||
                latestAdminLog.targetTitle ||
                latestAdminLog.targetTransactionUser ||
                latestAdminLog.targetUid ||
                latestAdminLog.targetId ||
                "—"
              : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            By: {latestAdminLog ? latestAdminLog.adminEmail || "—" : "—"}
          </small>

          <small style={{ display: "block", marginTop: 4 }}>
            Date: {latestAdminLog ? latestAdminLog.createdAtText || "—" : "—"}
          </small>
        </div>

        <div className="stat-card">
          <span>Admin Wallet Adjustments</span>
          <strong>{totalAdminWalletAdjustments.toFixed(3)} KWD</strong>
        </div>
        <div className="stat-card">
          <span>Refund Credits</span>
          <strong>{totalRefundCredits}</strong>
        </div>
        <div className="stat-card">
          <span>Promo Credits</span>
          <strong>{totalPromoCredits}</strong>
        </div>
        </section>

<section className={activeSection === "dashboard" ? "table-card" : "hidden-section"}>
  <div className="table-header">
    <div>
      <h2>Recent Auctions</h2>
      <p>Latest auctions created on the platform.</p>
    </div>
  </div>

  {recentAuctions.length === 0 ? (
    <p className="empty-text">No recent auctions found.</p>
  ) : (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Title</th>
            <th>Seller</th>
            <th>Status</th>
            <th>Live</th>
            <th>Current Bid</th>
            <th>Created</th>
          </tr>
        </thead>

        <tbody>
          {recentAuctions.map((auction) => (
            <tr key={auction.id}>
              <td>{auction.title || "Untitled Auction"}</td>
              <td>{auction.createdByUsername || auction.createdBy || "—"}</td>
              <td>
                <span className={`badge ${auction.status || "unknown"}`}>
                  {auction.status || "unknown"}
                </span>
              </td>
              <td>{auction.live ? "Yes" : "No"}</td>
              <td>{Number(auction.currentBid || 0).toFixed(3)} KWD</td>
              <td>
                {auction.createdAt?.seconds
                  ? new Date(auction.createdAt.seconds * 1000).toLocaleString()
                  : auction.createdAt
                  ? new Date(Number(auction.createdAt)).toLocaleString()
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )}
</section>

<section className={activeSection === "dashboard" ? "table-card" : "hidden-section"}>
  <div className="table-header">
    <div>
      <h2>Recent Transactions</h2>
      <p>Latest wallet and payment activity.</p>
    </div>
  </div>

  {recentTransactions.length === 0 ? (
    <p className="empty-text">No recent transactions found.</p>
  ) : (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>User</th>
            <th>Type</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Provider</th>
            <th>Date</th>
          </tr>
        </thead>

        <tbody>
          {recentTransactions.map((transaction) => (
            <tr key={transaction.id}>
              <td>{getUserLabel(transaction.uid)}</td>
              <td>{transaction.type || "—"}</td>

              <td
                className={
                  Number(transaction.amountKWD || 0) < 0
                    ? "amount-negative"
                    : "amount-positive"
                }
              >
                {Number(transaction.amountKWD || 0).toFixed(3)} KWD
              </td>

              <td>
                <span className={`badge ${transaction.status || "unknown"}`}>
                  {transaction.status || "unknown"}
                </span>
              </td>

              <td>{transaction.provider || "—"}</td>

              <td>
                {getTransactionTime(transaction)
                  ? new Date(
                      getTransactionTime(transaction)
                    ).toLocaleString()
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )}
</section>

<section className={activeSection === "dashboard" ? "table-card" : "hidden-section"}>
  <div className="table-header">
    <div>
      <h2>Recent Reports</h2>
      <p>Latest reported auctions requiring review.</p>
    </div>
  </div>

  {recentReports.length === 0 ? (
    <p className="empty-text">No recent reports found.</p>
  ) : (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Auction</th>
            <th>Seller</th>
            <th>Status</th>
            <th>Reason</th>
            <th>Reported</th>
          </tr>
        </thead>

        <tbody>
          {recentReports.map((auction) => (
            <tr key={auction.id}>
              <td>{auction.title || "Untitled Auction"}</td>

              <td>
                {auction.createdByUsername ||
                  auction.createdBy ||
                  "—"}
              </td>

              <td>
                <span className={`badge ${auction.status || "unknown"}`}>
                  {auction.status || "unknown"}
                </span>
              </td>

              <td>{auction.reportReason || "No reason provided"}</td>

              <td>
                {auction.reportedAt?.seconds
                  ? new Date(
                      auction.reportedAt.seconds * 1000
                    ).toLocaleString()
                  : auction.reportedAt
                  ? new Date(
                      Number(auction.reportedAt)
                    ).toLocaleString()
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )}
</section>


<section
  className={
    activeSection === "users"
      ? "table-card"
      : "hidden-section"
  }
>
  <div className="table-header">
    <div>
      <h2>Seller Ratings Overview</h2>

      <p>
        Monitor seller reputation using verified buyer ratings.
      </p>
    </div>

    <button
      className="enable-action-btn"
      onClick={handleExportSellerRatingsCsv}
    >
      Export Seller Ratings CSV
    </button>
  </div>

  <div className="seller-rating-summary-grid">
    <div className="seller-rating-summary-card">
      <span>Rated Sellers</span>

      <strong>
        {ratedSellers.length}
      </strong>

      <small>
        Sellers with at least one verified rating
      </small>
    </div>

    <div className="seller-rating-summary-card">
      <span>Platform Seller Average</span>

      <strong>
        {ratedSellers.length > 0
          ? (
              ratedSellers.reduce(
                (sum, seller) =>
                  sum +
                  Number(
                    seller.averageRating ||
                      0
                  ),
                0
              ) /
              ratedSellers.length
            ).toFixed(2)
          : "0.00"}
      </strong>

      <small>
        Average across rated sellers
      </small>
    </div>

    <div className="seller-rating-summary-card">
      <span>Total Verified Ratings</span>

      <strong>
        {ratedSellers.reduce(
          (sum, seller) =>
            sum +
            Number(
              seller.ratingCount || 0
            ),
          0
        )}
      </strong>

      <small>
        Verified buyer ratings submitted
      </small>
    </div>
  </div>

  <div className="seller-rankings-grid">
    <div className="seller-ranking-panel">
      <div className="seller-ranking-header">
        <div>
          <h3>Top Rated Sellers</h3>

          <p>
            Highest seller averages. Rating count breaks ties.
          </p>
        </div>

        <span className="seller-ranking-badge positive">
          Top 10
        </span>
      </div>

      {topRatedSellers.length === 0 ? (
        <p className="empty-text">
          No seller ratings available yet.
        </p>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Seller</th>
                <th>Rating</th>
                <th>Ratings</th>
                <th>Auctions</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {topRatedSellers.map(
                (seller, index) => (
                  <tr key={seller.id}>
                    <td>
                      <strong>
                        #{index + 1}
                      </strong>
                    </td>

                    <td>
                      <strong>
                        {seller.username ||
                          "Unnamed Seller"}
                      </strong>

                      <div className="small-muted">
                        {seller.email || "—"}
                      </div>
                    </td>

                    <td>
                      <div className="admin-rating-cell">
                        <span className="admin-rating-star">
                          ★
                        </span>

                        <strong>
                          {Number(
                            seller.averageRating ||
                              0
                          ).toFixed(2)}
                        </strong>

                        <span className="small-muted">
                          / 5
                        </span>
                      </div>
                    </td>

                    <td>
                      {Number(
                        seller.ratingCount ||
                          0
                      )}
                    </td>

                    <td>
                      {
                        auctions.filter(
                          (auction) =>
                            auction.createdBy ===
                              seller.email ||
                            auction.createdByUsername ===
                              seller.username
                        ).length
                      }
                    </td>

                    <td>
                      <button
                        className="enable-action-btn"
                        onClick={() => {
                          handleViewUserDetails(
                            seller
                          );
                        }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>

    <div className="seller-ranking-panel">
      <div className="seller-ranking-header">
        <div>
          <h3>Lowest Rated Sellers</h3>

          <p>
            Lowest seller averages for reputation review.
          </p>
        </div>

        <span className="seller-ranking-badge review">
          Review
        </span>
      </div>

      {lowestRatedSellers.length === 0 ? (
        <p className="empty-text">
          No seller ratings available yet.
        </p>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Seller</th>
                <th>Rating</th>
                <th>Ratings</th>
                <th>Auctions</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {lowestRatedSellers.map(
                (seller, index) => (
                  <tr key={seller.id}>
                    <td>
                      <strong>
                        #{index + 1}
                      </strong>
                    </td>

                    <td>
                      <strong>
                        {seller.username ||
                          "Unnamed Seller"}
                      </strong>

                      <div className="small-muted">
                        {seller.email || "—"}
                      </div>
                    </td>

                    <td>
                      <div className="admin-rating-cell">
                        <span className="admin-rating-star">
                          ★
                        </span>

                        <strong>
                          {Number(
                            seller.averageRating ||
                              0
                          ).toFixed(2)}
                        </strong>

                        <span className="small-muted">
                          / 5
                        </span>
                      </div>
                    </td>

                    <td>
                      {Number(
                        seller.ratingCount ||
                          0
                      )}
                    </td>

                    <td>
                      {
                        auctions.filter(
                          (auction) =>
                            auction.createdBy ===
                              seller.email ||
                            auction.createdByUsername ===
                              seller.username
                        ).length
                      }
                    </td>

                    <td>
                      <button
                        className="flag-action-btn"
                        onClick={() => {
                          handleViewUserDetails(
                            seller
                          );
                        }}
                      >
                        Review Seller
                      </button>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
</section>

<section className={activeSection === "users" ? "table-card" : "hidden-section"}>

<div className="table-header">
  <div>
    <h2>Users</h2>
    <p>Search and filter real users loaded from Firestore.</p>
  </div>

  <button
    className="enable-action-btn"
    onClick={handleExportUsersCsv}
  >
    Export Users CSV
  </button>
</div>

        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 14,
          }}
        >
          <div className="mini-stat-card">
            <span>Active</span>
            <strong>
              {users.filter((user) => user.archived !== true).length}
            </strong>
          </div>

          <div className="mini-stat-card">
            <span>Archived</span>
            <strong>
              {users.filter((user) => user.archived === true).length}
            </strong>
          </div>

          <div className="mini-stat-card">
            <span>Wallet +</span>
            <strong>
              {
                users.filter(
                  (user) =>
                    Number(user.walletBalanceKWD || 0) > 0
                ).length
              }
            </strong>
          </div>

          <div className="mini-stat-card">
            <span>Credits +</span>
            <strong>
              {
                users.filter(
                  (user) =>
                    Number(user.credits?.["15"] || 0) +
                      Number(user.credits?.["30"] || 0) +
                      Number(user.credits?.["60"] || 0) +
                      Number(user.credits?.ext15 || 0) >
                    0
                ).length
              }
            </strong>
          </div>
        </div>

        <div className="controls-row">
          <input
            className="search-input"
            placeholder="Search username, email, or phone..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
          />

          <div className="filter-buttons">
            <button className={userPhoneFilter === "all" ? "filter-btn active" : "filter-btn"} onClick={() => setUserPhoneFilter("all")}>All</button>
            <button className={userPhoneFilter === "verified" ? "filter-btn active" : "filter-btn"} onClick={() => setUserPhoneFilter("verified")}>Verified</button>
            <button className={userPhoneFilter === "notVerified" ? "filter-btn active" : "filter-btn"} onClick={() => setUserPhoneFilter("notVerified")}>Not Verified</button>
            <button className={userPhoneFilter === "admins" ? "filter-btn active" : "filter-btn"} onClick={() => setUserPhoneFilter("admins")}>Admins Only</button>
            <button className={userPhoneFilter === "banned" ? "filter-btn active" : "filter-btn"} onClick={() => setUserPhoneFilter("banned")}>Banned Only</button>
            <button className={userPhoneFilter === "disabled" ? "filter-btn active" : "filter-btn"} onClick={() => setUserPhoneFilter("disabled")}>Disabled Only</button>
            <button className={userPhoneFilter === "walletPositive" ? "filter-btn active" : "filter-btn"} onClick={() => setUserPhoneFilter("walletPositive")}>Wallet Positive</button>
            <button className={userPhoneFilter === "walletNegative" ? "filter-btn active" : "filter-btn"} onClick={() => setUserPhoneFilter("walletNegative")}>Negative Wallet</button>
            <button className={userPhoneFilter === "hasCredits" ? "filter-btn active" : "filter-btn"} onClick={() => setUserPhoneFilter("hasCredits")}>Has Credits</button>
            <button className={userPhoneFilter === "archived" ? "filter-btn active" : "filter-btn"} onClick={() => setUserPhoneFilter("archived")}>Archived Only</button>
            <button
              className={userSortMode === "walletHigh" ? "filter-btn active" : "filter-btn"}
              onClick={() =>
                setUserSortMode(
                  userSortMode === "walletHigh"
                    ? "default"
                    : "walletHigh"
                )
              }
            >
              Sort Wallet ↓
            </button>

            <button
              className={userSortMode === "signupDate" ? "filter-btn active" : "filter-btn"}
              onClick={() =>
                setUserSortMode(
                  userSortMode === "signupDate"
                    ? "default"
                    : "signupDate"
                )
              }
            >
              Sort Signup Date ↓
            </button>

            <button
              className={userSortMode === "creditsHigh" ? "filter-btn active" : "filter-btn"}
              onClick={() =>
                setUserSortMode(
                  userSortMode === "creditsHigh"
                    ? "default"
                    : "creditsHigh"
                )
              }
            >
              Sort Credits ↓
            </button>

            <button
              className="filter-btn"
              onClick={() => {
                setUserSearch("");
                setUserPhoneFilter("all");
              }}
            >
              Reset Filters
            </button>
          </div>
        </div>

        {loading ? (
          <p className="loading-text">Loading users...</p>
        ) : filteredUsers.length === 0 ? (
          <p className="empty-text">No users match this filter.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>UID</th>
                  <th>Phone</th>
                  <th>Phone Status</th>
                  <th>Signup Date</th>
                  <th>Credits</th>
                  <th>Wallet</th>
                  <th>Seller Rating</th>
                  <th>Ratings</th>
                  <th>Last Admin Note</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>{user.username || "—"}</td>
                    <td>{user.email || "—"}</td>
                    <td className="small-muted">{user.uid || user.id || "—"}</td>
                    <td>{user.phoneNumber || "—"}</td>
                    <td>
                      <span className={user.phoneVerified ? "badge verified" : "badge not-verified"}>
                        {user.phoneVerified ? "Verified" : "Not Verified"}
                      </span>
                    </td>

                    <td>
                      {user.createdAt?.seconds
                        ? new Date(user.createdAt.seconds * 1000).toLocaleString()
                        : "—"}
                    </td>

                    <td className="small-muted">
                      <div>15: {Number(user.credits?.["15"] || 0)}</div>
                      <div>30: {Number(user.credits?.["30"] || 0)}</div>
                      <div>60: {Number(user.credits?.["60"] || 0)}</div>
                      <div>ext15: {Number(user.credits?.ext15 || 0)}</div>
                    </td>

                    <td>
                      {Number(
                        user.walletBalanceKWD || 0
                      ).toFixed(3)} KWD
                    </td>

                    <td>
                      {Number(
                        user.ratingCount || 0
                      ) > 0 ? (
                        <div className="admin-rating-cell">
                          <span className="admin-rating-star">
                            ★
                          </span>

                          <strong>
                            {Number(
                              user.averageRating || 0
                            ).toFixed(1)}
                          </strong>

                          <span className="small-muted">
                            / 5
                          </span>
                        </div>
                      ) : (
                        <span className="small-muted">
                          No ratings
                        </span>
                      )}
                    </td>

                    <td>
                      {Number(
                        user.ratingCount || 0
                      )}
                    </td>

                    <td className="small-muted">
                      {user.lastAdminNote || "—"}
                    </td>

<td>

<button
                      className="flag-action-btn"
                      onClick={() =>
                        handleViewUserDetails(
                          user
                        )
                      }
                      style={{
                        marginRight: 6,
                      }}
                    >
                      View Details
                    </button>

                    <button
                        className={user.disabled ? "enable-action-btn" : "danger-action-btn"}
                        onClick={() => handleToggleDisableUser(user.id, user.disabled === true)}
                        style={{ marginRight: 6 }}
                      >
                        {user.disabled ? "Enable" : "Disable"}
                      </button>

                      <button
                        className={user.banned ? "enable-action-btn" : "remove-action-btn"}
                        onClick={() => handleToggleBanUser(user.id, user.banned === true)}
                        style={{ marginRight: 6 }}
                      >
                        {user.banned ? "Unban" : "Ban"}
                      </button>

                      <button
                        className={user.isAdmin ? "remove-action-btn" : "flag-action-btn"}
                        onClick={() => handleToggleAdminRole(user.id, user.isAdmin === true)}
                        style={{ marginRight: 6 }}
                      >
                        {user.isAdmin ? "Remove Admin" : "Make Admin"}
                      </button>

                      <button
                        className="danger-action-btn"
                        onClick={() => handleResetWallet(user.id)}
                        style={{ marginLeft: 6 }}
                      >
                        Reset Wallet
                      </button>
                      <button
                        className="promo-action-btn"
                        onClick={() => handleAddPromotionalBalance(user.id)}
                        style={{ marginLeft: 6 }}
                      >
                        Add Balance
                      </button>
                      <button
                        className="refund-action-btn"
                        onClick={() => handleRefundAsCredit(user.id)}
                        style={{ marginLeft: 6 }}
                      >
                        Refund Credit
                      </button>
                      <button
                        className="promo-action-btn"
                        onClick={() => handleAddPromotionalCredit(user.id)}
                        style={{ marginLeft: 6 }}
                      >
                        Promo Credit
                      </button>
                      {user.archived !== true ? (
                        <button
                          className="remove-action-btn"
                          onClick={() => handleArchiveUser(user.id)}
                          style={{ marginLeft: 6 }}
                        >
                          Archive
                        </button>
                      ) : (
                        <button
                          className="enable-action-btn"
                          onClick={() => handleUnarchiveUser(user.id)}
                          style={{ marginLeft: 6 }}
                        >
                          Unarchive
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {activeSection === "users" && selectedUserDetails && (
        <section className={activeSection === "users" ? "table-card" : "hidden-section"}>
          <div className="table-header">
            <div>
              <h2>User Details</h2>
              <p>Inspect user account data before taking admin action.</p>
            </div>

            <button
              className="filter-btn"
              onClick={() => {
                setSelectedUserDetails(
                  null
                );

                setSelectedUserRatings(
                  []
                );

                setSelectedUserRatingsLoading(
                  false
                );
              }}
            >
              Close
            </button>
          </div>

          <div className="table-wrapper">
            <table>
              <tbody>
                <tr>
                  <th>Username</th>
                  <td>{selectedUserDetails.username || "—"}</td>
                </tr>

                <tr>
                  <th>Email</th>
                  <td>{selectedUserDetails.email || "—"}</td>
                </tr>

                <tr>
                  <th>UID</th>
                  <td className="small-muted">
                    {selectedUserDetails.uid || selectedUserDetails.id || "—"}
                  </td>
                </tr>

                <tr>
                  <th>Phone</th>
                  <td>{selectedUserDetails.phoneNumber || "—"}</td>
                </tr>

                <tr>
                  <th>Signup Date</th>
                  <td>
                    {selectedUserDetails.createdAt?.seconds
                      ? new Date(
                          selectedUserDetails.createdAt.seconds * 1000
                        ).toLocaleString()
                      : "—"}
                  </td>
                </tr>

                <tr>
                  <th>Wallet</th>

                  <td>
                    {Number(
                      selectedUserDetails.walletBalanceKWD ||
                        0
                    ).toFixed(3)} KWD
                  </td>
                </tr>

                <tr>
                  <th>Seller Reputation</th>

                  <td>
                    {Number(
                      selectedUserDetails.ratingCount ||
                        0
                    ) > 0 ? (
                      <div className="admin-seller-rating-card">
                        <div className="admin-seller-rating-score">
                          <span className="admin-seller-rating-star">
                            ★
                          </span>

                          <strong>
                            {Number(
                              selectedUserDetails.averageRating ||
                                0
                            ).toFixed(1)}
                          </strong>

                          <span>
                            / 5
                          </span>
                        </div>

                        <div className="admin-seller-rating-stars">
                          {[1, 2, 3, 4, 5].map(
                            (star) => (
                              <span
                                key={`admin-rating-${star}`}
                                className={
                                  star <=
                                  Math.round(
                                    Number(
                                      selectedUserDetails.averageRating ||
                                        0
                                    )
                                  )
                                    ? "admin-rating-star-filled"
                                    : "admin-rating-star-empty"
                                }
                              >
                                ★
                              </span>
                            )
                          )}
                        </div>

                        <div className="admin-seller-rating-meta">
                          {Number(
                            selectedUserDetails.ratingCount ||
                              0
                          )}{" "}
                          {Number(
                            selectedUserDetails.ratingCount ||
                              0
                          ) === 1
                            ? "verified buyer rating"
                            : "verified buyer ratings"}
                        </div>
                      </div>
                    ) : (
                      <div className="admin-seller-rating-empty">
                        <strong>No Seller Ratings Yet</strong>

                        <span>
                          This user has not received a verified buyer rating.
                        </span>
                      </div>
                    )}
                  </td>
                  </tr>

<tr>
  <th>Rating Breakdown</th>

  <td>
    {selectedUserRatingsLoading ? (
      <div className="admin-rating-loading">
        Loading seller ratings...
      </div>
    ) : selectedUserRatings.length === 0 ? (
      <div className="admin-rating-history-empty">
        No rating records are available for this seller.
      </div>
    ) : (
      <div className="admin-rating-breakdown">
        {[5, 4, 3, 2, 1].map(
          (ratingValue) => {
            const ratingCount =
              selectedUserRatings.filter(
                (rating) =>
                  Number(
                    rating.rating ||
                      0
                  ) ===
                  ratingValue
              ).length;

            const percentage =
              selectedUserRatings.length >
              0
                ? (
                    ratingCount /
                    selectedUserRatings.length
                  ) *
                  100
                : 0;

            return (
              <div
                key={`rating-breakdown-${ratingValue}`}
                className="admin-rating-breakdown-row"
              >
                <div className="admin-rating-breakdown-label">
                  {ratingValue} ★
                </div>

                <div className="admin-rating-breakdown-track">
                  <div
                    className="admin-rating-breakdown-fill"
                    style={{
                      width: `${percentage}%`,
                    }}
                  />
                </div>

                <div className="admin-rating-breakdown-count">
                  {ratingCount}
                </div>
              </div>
            );
          }
        )}
      </div>
    )}
  </td>
</tr>

<tr>
  <th>Seller Rating History</th>

  <td>
    {selectedUserRatingsLoading ? (
      <div className="admin-rating-loading">
        Loading rating history...
      </div>
    ) : selectedUserRatings.length === 0 ? (
      <div className="admin-rating-history-empty">
        This seller has not received any verified buyer ratings yet.
      </div>
    ) : (
      <div className="admin-rating-history-list">
        {selectedUserRatings.map(
          (rating) => {
            const numericRating =
              Math.min(
                5,
                Math.max(
                  0,
                  Number(
                    rating.rating ||
                      0
                  )
                )
              );

            const ratingDate =
              rating.createdAt?.seconds
                ? new Date(
                    rating.createdAt
                      .seconds *
                      1000
                  ).toLocaleString()
                : rating.createdAtMs
                ? new Date(
                    Number(
                      rating.createdAtMs
                    )
                  ).toLocaleString()
                : rating.timestamp
                ? new Date(
                    Number(
                      rating.timestamp
                    )
                  ).toLocaleString()
                : "—";

            return (
              <div
                key={rating.id}
                className="admin-rating-history-card"
              >
                <div className="admin-rating-history-top">
                  <div className="admin-rating-history-stars">
                    {[1, 2, 3, 4, 5].map(
                      (
                        star
                      ) => (
                        <span
                          key={`${rating.id}-star-${star}`}
                          className={
                            star <=
                            numericRating
                              ? "admin-rating-star-filled"
                              : "admin-rating-star-empty"
                          }
                        >
                          ★
                        </span>
                      )
                    )}
                  </div>

                  <strong className="admin-rating-history-score">
                    {numericRating.toFixed(
                      0
                    )}{" "}
                    / 5
                  </strong>
                </div>

                <div className="admin-rating-history-grid">
                  <div>
                    <span>
                      Auction
                    </span>

                    <strong>
                      {rating.auctionTitle ||
                        "Untitled Auction"}
                    </strong>

                    <small>
                      {rating.auctionId ||
                        "—"}
                    </small>
                  </div>

                  <div>
                    <span>
                      Rated By
                    </span>

                    <strong>
                      {rating.raterUsername
                        ? `@${rating.raterUsername}`
                        : "Unknown Buyer"}
                    </strong>

                    <small>
                      {rating.raterUid ||
                        "—"}
                    </small>
                  </div>

                  <div>
                    <span>
                      Submitted
                    </span>

                    <strong>
                      {ratingDate}
                    </strong>

                    <small>
                      Verified bidder rating
                    </small>
                  </div>
                </div>
              </div>
            );
          }
        )}
      </div>
    )}
  </td>
</tr>

<tr>
  <th>Credits</th>
                  <td className="small-muted">
                    15: {Number(selectedUserDetails.credits?.["15"] || 0)} |{" "}
                    30: {Number(selectedUserDetails.credits?.["30"] || 0)} |{" "}
                    60: {Number(selectedUserDetails.credits?.["60"] || 0)} |{" "}
                    ext15: {Number(selectedUserDetails.credits?.ext15 || 0)}
                  </td>
                </tr>

                <tr>
                  <th>Status</th>
                  <td>
                    Admin: {selectedUserDetails.isAdmin ? "Yes" : "No"} |{" "}
                    Banned: {selectedUserDetails.banned ? "Yes" : "No"} |{" "}
                    Disabled: {selectedUserDetails.disabled ? "Yes" : "No"}
                  </td>
                </tr>

                <tr>
                  <th>Last Login</th>
                  <td>
                    {selectedUserDetails.lastLoginAt?.seconds
                      ? new Date(
                          selectedUserDetails.lastLoginAt.seconds * 1000
                        ).toLocaleString()
                      : selectedUserDetails.lastLoginAtMs
                      ? new Date(
                          Number(selectedUserDetails.lastLoginAtMs)
                        ).toLocaleString()
                      : "—"}
                  </td>
                </tr>

                <tr>
                  <th>Current Device</th>
                  <td>
                    {selectedUserDetails.deviceName || "—"}
                  </td>
                </tr>

                <tr>
                  <th>Device Model</th>
                  <td>
                    {selectedUserDetails.deviceModel || "—"}
                  </td>
                </tr>

                <tr>
                  <th>Platform</th>
                  <td>
                    {selectedUserDetails.platform || "—"}
                  </td>
                </tr>

                <tr>
                  <th>OS Version</th>
                  <td>
                    {selectedUserDetails.osVersion || "—"}
                  </td>
                </tr>

                <tr>
                  <th>Current Session ID</th>
                  <td className="small-muted">
                    {selectedUserDetails.currentSessionId || "—"}
                  </td>
                </tr>

                <tr>
                  <th>Last Installation ID</th>
                  <td className="small-muted">
                    {selectedUserDetails.lastInstallationId || "—"}
                  </td>
                </tr>

                <tr>
                  <th>Installation Count</th>
                  <td>
                    {selectedUserDetails.installationIds?.length || 0}
                  </td>
                </tr>

                <tr>
                  <th>Auctions Created</th>
                  <td>
                    {
                      auctions.filter(
                        (auction) =>
                          auction.createdBy === selectedUserDetails.email ||
                          auction.createdByUsername === selectedUserDetails.username
                      ).length
                    }
                  </td>
                </tr>

                <tr>
  <th>Transactions</th>
  <td>
    {
      transactions.filter(
        (transaction) =>
          transaction.uid === selectedUserDetails.uid ||
          transaction.uid === selectedUserDetails.id
      ).length
    }
  </td>
</tr>

<tr>
  <th>Admin Notes</th>
  <td>
    {selectedUserDetails.adminNotes?.length
      ? selectedUserDetails.adminNotes
          .map(
            (item, index) =>
              `${index + 1}. ${item.note || "No note"} — ${
                item.adminEmail || "Unknown admin"
              } — ${
                item.createdAt
                  ? new Date(Number(item.createdAt)).toLocaleString()
                  : "Unknown date"
              }`
          )
          .join(" | ")
      : "—"}

    <br />

    <button
      className="flag-action-btn"
      onClick={handleAddUserAdminNote}
      style={{ marginTop: 10 }}
    >
      Add Admin Note
    </button>
  </td>
</tr>
              </tbody>
            </table>
          </div>
        </section>
      )}

<section className={activeSection === "auctions" ? "table-card" : "hidden-section"}>
        <div className="table-header">
          <div>
            <h2>Flagged Auctions</h2>
            <p>Quick review list for auctions marked as suspicious or needing attention.</p>
          </div>
        </div>

        {dateFilteredAuctions.filter((auction) => auction.flagged === true).length === 0 ? (
          <p className="empty-text">No flagged auctions right now.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                <th>Auction ID</th>
                <th>Title</th>
                  <th>Seller</th>
                  <th>Seller Email</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>Flagged At</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
              {dateFilteredAuctions
                  .filter((auction) => auction.flagged === true)
                  .sort((a, b) => Number(b.flaggedAt || 0) - Number(a.flaggedAt || 0))
                  .map((auction) => (
                    <tr key={auction.id}>
                    <td className="small-muted">
                      {auction.id}
                    </td>

                    <td>
                      <strong>{auction.title || "Untitled"}</strong>
                    </td>

                      <td>{auction.createdByUsername || auction.createdBy || "—"}</td>

                      <td>
                      {auction.createdBy || "—"}
                      </td>

                      <td>
                        <span className={`badge ${auction.status || "unknown"}`}>
                          {auction.status || "unknown"}
                        </span>
                      </td>

                      <td className="small-muted">
                        {auction.flagReason || "No reason provided"}
                      </td>

                      <td className="small-muted">
                        {auction.flaggedAt
                          ? new Date(
                              Number(auction.flaggedAt)
                            ).toLocaleString()
                          : "—"}
                      </td>

                      <td>
                        {auction.status !== "closed" && auction.status !== "removed" ? (
                          <>
                            <button
                              className="danger-action-btn"
                              onClick={() => handleCloseAuction(auction.id)}
                              style={{ marginRight: 6 }}
                            >
                              Close
                            </button>

                            <button
                              className="remove-action-btn"
                              onClick={() => handleRemoveAuction(auction.id)}
                              style={{ marginRight: 6 }}
                            >
                              Remove
                            </button>

                            <button
                              className="flag-action-btn"
                              onClick={() => handleUnflagAuction(auction.id)}
                            >
                              Unflag
                            </button>
                          </>
                        ) : (
                          <span className="small-muted">{auction.status}</span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className={activeSection === "auctions" ? "table-card" : "hidden-section"}>
      <div className="table-header">
  <div>
    <h2>Auctions</h2>
    <p>Filter all auction listings loaded from Firestore.</p>
  </div>

  <button
  className="enable-action-btn"
  onClick={handleExportAuctionsCsv}
  style={{ marginRight: 8 }}
>
  Export Auctions CSV
</button>

<button
  className="promo-action-btn"
  onClick={handleExportRevenueCsv}
>
  Export Revenue CSV
</button>
</div>

        <div className="controls-row">
        <input
            className="search-input"
            placeholder="Search seller username or email..."
            value={auctionSearch}
            onChange={(e) => setAuctionSearch(e.target.value)}
          />
          <div className="filter-buttons">
          <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <div className="summary-pill">
            Open: {auctions.filter((auction) => auction.status === "open").length}
          </div>

          <div className="summary-pill">
            Scheduled: {auctions.filter((auction) => auction.status === "scheduled").length}
          </div>

          <div className="summary-pill">
            Live: {auctions.filter((auction) => auction.live === true).length}
          </div>

          <div className="summary-pill">
            Closed: {auctions.filter((auction) => auction.status === "closed").length}
          </div>

          <div className="summary-pill">
            Removed: {auctions.filter((auction) => auction.status === "removed").length}
          </div>

          <div className="summary-pill">
            Flagged: {auctions.filter((auction) => auction.flagged === true).length}
          </div>
        </div>
            <button className={auctionStatusFilter === "all" ? "filter-btn active" : "filter-btn"} onClick={() => setAuctionStatusFilter("all")}>All</button>
            <button className={auctionStatusFilter === "open" ? "filter-btn active" : "filter-btn"} onClick={() => setAuctionStatusFilter("open")}>Open</button>
            <button className={auctionStatusFilter === "scheduled" ? "filter-btn active" : "filter-btn"} onClick={() => setAuctionStatusFilter("scheduled")}>Scheduled</button>
            <button className={auctionStatusFilter === "live" ? "filter-btn active" : "filter-btn"} onClick={() => setAuctionStatusFilter("live")}>Live Only</button>
            <button className={auctionStatusFilter === "closed" ? "filter-btn active" : "filter-btn"} onClick={() => setAuctionStatusFilter("closed")}>Closed</button>
            <button className={auctionStatusFilter === "removed" ? "filter-btn active" : "filter-btn"} onClick={() => setAuctionStatusFilter("removed")}>Removed</button>
            <button className={auctionStatusFilter === "flagged" ? "filter-btn active" : "filter-btn"} onClick={() => setAuctionStatusFilter("flagged")}>Flagged Only</button>
            <button className={auctionStatusFilter === "reported" ? "filter-btn active" : "filter-btn"} onClick={() => setAuctionStatusFilter("reported")}>Reported Only</button>
            <button
              className="filter-btn"
              onClick={() => {
                setAuctionStatusFilter("all");
                setAuctionSearch("");
              }}
            >
              Reset Filters
            </button>
          </div>
        </div>

        {loading ? (
          <p className="loading-text">Loading auctions...</p>
        ) : filteredAuctions.length === 0 ? (
          <p className="empty-text">No auctions match this filter.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                <th>Image</th>
                  <th>Auction ID</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Seller</th>
                  <th>Seller Email</th>
                  <th>Status</th>
                  <th>Closure Reason</th>
                  <th>Live</th>
                  <th>Created At</th>
                  <th>Current Bid</th>
                  <th>Total Bids</th>
                  <th>Reports</th>
                  <th>Reported At</th>
                  <th>Winner</th>
                  <th>Duration</th>
                  <th>Time Remaining</th>
                  <th>Total Revenue</th>
                  <th>Last Admin Note</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAuctions.map((auction) => (
                  <tr key={auction.id}>
                    <td>{auction.image ? <img src={auction.image} alt={auction.title || "Auction"} className="auction-thumb" /> : "—"}</td>

               <td className="small-muted">
               {auction.id}
               </td>

              <td>
               <strong>{auction.title || "Untitled"}</strong>
              <div className="small-muted">{auction.description || "No description"}</div>
              </td>
                    <td>{auction.category || "—"}</td>
                    <td>{auction.createdByUsername || auction.createdBy || "—"}</td>

                   <td>
                   {auction.createdBy || "—"}
                   </td>

                   <td><span className={`badge ${auction.status || "unknown"}`}>{auction.status || "unknown"}</span></td>

                   <td className="small-muted">
                      {formatClosureReasonLabel(auction.closureReason)}
                    </td>

<td>{auction.live ? "Yes" : "No"}</td>

<td>
  {auction.createdAt?.seconds
    ? new Date(
        auction.createdAt.seconds * 1000
      ).toLocaleString()
    : auction.createdAt
    ? new Date(
        Number(auction.createdAt)
      ).toLocaleString()
    : "—"}
</td>

<td>{Number(auction.currentBid || 0).toFixed(3)}</td>

<td>
{auction.bidHistory?.length ||
 auction.bids?.length ||
auction.bidAmounts?.length ||
0}
</td>

                    <td>
  {Number(auction.reportCount || 0)}
</td>

<td>
  {auction.reportedAt
    ? new Date(Number(auction.reportedAt)).toLocaleString()
    : "—"}
</td>

<td>{auction.currentWinner || auction.winner || "—"}</td>

<td>
  {auction.durationMinutes
    ? `${auction.durationMinutes} min`
    : "—"}
</td>

<td className="small-muted">
  {formatAuctionTimeRemaining(auction)}
</td>

<td>
  {(
    Number(auction.billingAmountKWD || 0) +
    Number(auction.extensionTotalAmountKWD || 0)
  ).toFixed(3)} KWD
</td>

<td className="small-muted">
  {auction.lastAdminNote || "—"}
</td>

<td>
  <button
    className="enable-action-btn"
    onClick={() => handleViewAuctionDetails(auction)}
    style={{ marginRight: 6 }}
  >
    View Details
  </button>

  {auction.status !== "closed" && auction.status !== "removed" && (
    <>
      <button
        className="danger-action-btn"
        onClick={() => handleCloseAuction(auction.id)}
        style={{ marginRight: 6 }}
      >
        Close
      </button>

      <button
        className="remove-action-btn"
        onClick={() => handleRemoveAuction(auction.id)}
        style={{ marginRight: 6 }}
      >
        Remove
      </button>

      <button
        className="flag-action-btn"
        onClick={() => handleFlagAuction(auction.id)}
      >
        {auction.flagged ? "Flagged" : "Flag"}
      </button>
    </>
  )}

  {(auction.status === "closed" || auction.status === "removed") && (
    <span className="small-muted">{auction.status}</span>
  )}
</td>



                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {activeSection === "auctions" && selectedAuctionDetails && (
  <section className="table-card">
    <div className="table-header">
      <div>
        <h2>Auction Audit Details</h2>
        <p>Full admin audit view for this auction.</p>
      </div>

      <button
        className="filter-btn"
        onClick={() => setSelectedAuctionDetails(null)}
      >
        Close Details
      </button>
    </div>

    {selectedAuctionDetails.image ? (
      <img
        src={selectedAuctionDetails.image}
        alt={selectedAuctionDetails.title || "Auction"}
        className="audit-auction-image"
      />
    ) : null}

    <div className="details-grid">
      <div>
        <strong>Title</strong>
        <p>{selectedAuctionDetails.title || "Untitled Auction"}</p>
      </div>

      <div>
        <strong>Category</strong>
        <p>{selectedAuctionDetails.category || "—"}</p>
      </div>

      <div>
        <strong>Full Description</strong>
        <p>{selectedAuctionDetails.description || "No description"}</p>
      </div>

      <div>
        <strong>Auction ID</strong>
        <p>{selectedAuctionDetails.id || "—"}</p>
      </div>

      <div>
        <strong>Seller Username</strong>
        <p>{selectedAuctionDetails.createdByUsername || "—"}</p>
      </div>

      <div>
        <strong>Seller Email</strong>
        <p>{selectedAuctionDetails.createdBy || "—"}</p>
      </div>

      <div>
        <strong>Status</strong>
        <p>{selectedAuctionDetails.status || "unknown"}</p>
      </div>

      <div>
        <strong>Live</strong>
        <p>{selectedAuctionDetails.live ? "Yes" : "No"}</p>
      </div>

      <div>
        <strong>Duration</strong>
        <p>
          {selectedAuctionDetails.durationMinutes
            ? `${selectedAuctionDetails.durationMinutes} min`
            : "—"}
        </p>
      </div>

      <div>
        <strong>Current Bid</strong>
        <p>
          {Number(selectedAuctionDetails.currentBid || 0).toFixed(3)} KWD
        </p>
      </div>

      <div>
        <strong>Winner / Top Bidder</strong>
        <p>
          {selectedAuctionDetails.currentWinner ||
            selectedAuctionDetails.winner ||
            "—"}
        </p>
      </div>

      <div>
        <strong>Total Bid Buttons / Bids</strong>
        <p>
          {selectedAuctionDetails.bidHistory?.length ||
            selectedAuctionDetails.bids?.length ||
            selectedAuctionDetails.bidAmounts?.length ||
            0}
        </p>
      </div>

      <div>
        <strong>Created At</strong>
        <p>
          {selectedAuctionDetails.createdAt?.seconds
            ? new Date(
                selectedAuctionDetails.createdAt.seconds * 1000
              ).toLocaleString()
            : selectedAuctionDetails.createdAt
            ? new Date(
                Number(selectedAuctionDetails.createdAt)
              ).toLocaleString()
            : "—"}
        </p>
      </div>

      <div>
        <strong>Scheduled Start</strong>
        <p>
          {selectedAuctionDetails.scheduledStartAtMs
            ? new Date(
                Number(selectedAuctionDetails.scheduledStartAtMs)
              ).toLocaleString()
            : "—"}
        </p>
      </div>

      <div>
        <strong>Started At</strong>
        <p>
          {selectedAuctionDetails.startedAtMs
            ? new Date(
                Number(selectedAuctionDetails.startedAtMs)
              ).toLocaleString()
            : "—"}
        </p>
      </div>

      <div>
        <strong>Ends At</strong>
        <p>
          {selectedAuctionDetails.endsAtMs
            ? new Date(
                Number(selectedAuctionDetails.endsAtMs)
              ).toLocaleString()
            : "—"}
        </p>
      </div>

      <div>
        <strong>Time Remaining</strong>
        <p>{formatAuctionTimeRemaining(selectedAuctionDetails)}</p>
      </div>

      <div>
        <strong>Report Count</strong>
        <p>{Number(selectedAuctionDetails.reportCount || 0)}</p>
      </div>

      <div>
        <strong>Reported</strong>
        <p>{selectedAuctionDetails.reported ? "Yes" : "No"}</p>
      </div>

      <div>
  <strong>Report Reason</strong>
  <p>{selectedAuctionDetails.reportReason || "—"}</p>
</div>

<div>
  <strong>Reported At</strong>
  <p>
    {selectedAuctionDetails.reportedAt?.seconds
      ? new Date(
          selectedAuctionDetails.reportedAt.seconds * 1000
        ).toLocaleString()
      : selectedAuctionDetails.reportedAt
      ? new Date(
          Number(selectedAuctionDetails.reportedAt)
        ).toLocaleString()
      : "—"}
  </p>
</div>

<div>
  <strong>Flagged</strong>
  <p>{selectedAuctionDetails.flagged ? "Yes" : "No"}</p>
</div>

<div>
  <strong>Flag Reason</strong>
  <p>{selectedAuctionDetails.flagReason || "—"}</p>
</div>

<div>
  <strong>Flagged At</strong>
  <p>
    {selectedAuctionDetails.flaggedAt
      ? new Date(
          Number(selectedAuctionDetails.flaggedAt)
        ).toLocaleString()
      : "—"}
  </p>
</div>

<div>
  <strong>Closed At</strong>
  <p>
    {selectedAuctionDetails.closedAt?.seconds
      ? new Date(
          selectedAuctionDetails.closedAt.seconds * 1000
        ).toLocaleString()
      : selectedAuctionDetails.closedAtMs
      ? new Date(
          Number(selectedAuctionDetails.closedAtMs)
        ).toLocaleString()
      : selectedAuctionDetails.closedAt
      ? new Date(
          Number(selectedAuctionDetails.closedAt)
        ).toLocaleString()
      : "—"}
  </p>
</div>

<div>
  <strong>Closure Reason</strong>
  <p>{formatClosureReasonLabel(selectedAuctionDetails.closureReason)}</p>
</div>

<div>
  <strong>Auto Closed By</strong>
  <p>{selectedAuctionDetails.autoClosedBy || "—"}</p>
</div>

<div>
  <strong>Closed By Admin</strong>
  <p>{selectedAuctionDetails.closedByAdmin ? "Yes" : "No"}</p>
</div>

<div>
  <strong>Closed By Admin Email</strong>
  <p>{selectedAuctionDetails.closedByAdminEmail || "—"}</p>
</div>

<div>
  <strong>Host Disconnect Reason</strong>
  <p>{selectedAuctionDetails.hostDisconnectedReason || "—"}</p>
</div>

<div>
  <strong>Removed At</strong>
  <p>
    {selectedAuctionDetails.removedAt
      ? new Date(
          Number(selectedAuctionDetails.removedAt)
        ).toLocaleString()
      : "—"}
  </p>
</div>

<div>
  <strong>Recording Status</strong>
  <p>
    {auctionRecordingDetails?.status ||
      selectedAuctionDetails.recordingStatus ||
      selectedAuctionDetails.lastRecording?.status ||
      "—"}
  </p>
</div>

<div>
  <strong>Recording Started At</strong>
  <p>
    {auctionRecordingDetails?.startedAt?.seconds
      ? new Date(
          auctionRecordingDetails.startedAt.seconds * 1000
        ).toLocaleString()
      : "—"}
  </p>
</div>

<div>
  <strong>Recording Stopped At</strong>
  <p>
    {auctionRecordingDetails?.stoppedAt?.seconds
      ? new Date(
          auctionRecordingDetails.stoppedAt.seconds * 1000
        ).toLocaleString()
      : selectedAuctionDetails.lastRecording?.stoppedAt?.seconds
      ? new Date(
          selectedAuctionDetails.lastRecording.stoppedAt.seconds * 1000
        ).toLocaleString()
      : "—"}
  </p>
</div>

<div>
  <strong>Recording SID</strong>
  <p>
    {auctionRecordingDetails?.sid ||
      selectedAuctionDetails.lastRecording?.sid ||
      "—"}
  </p>
</div>

<div>
  <strong>Recording Paths</strong>
  <p>
    {selectedAuctionDetails.storagePaths?.length
      ? selectedAuctionDetails.storagePaths.join(" | ")
      : selectedAuctionDetails.lastRecording?.paths?.length
      ? selectedAuctionDetails.lastRecording.paths.join(" | ")
      : "—"}
  </p>

  <button
    className="enable-action-btn"
    onClick={() => handleOpenRecording()}
    disabled={recordingLoading}
    style={{ marginTop: 10 }}
  >
    {recordingLoading ? "Opening..." : "Open Latest Recording"}
  </button>
</div>

<div style={{ gridColumn: "1 / -1" }}>
  <strong>Recording Sessions</strong>

  {auctionRecordingSessions.length === 0 ? (
    <p>—</p>
  ) : (
    <div className="table-wrapper" style={{ marginTop: 10 }}>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Status</th>
            <th>SID</th>
            <th>Started</th>
            <th>Stopped</th>
            <th>Files</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {auctionRecordingSessions.map((recording, index) => (
            <tr key={recording.id}>
              <td>{index + 1}</td>

              <td>
                <span className={`badge ${recording.status || "unknown"}`}>
                  {recording.status || "unknown"}
                </span>
              </td>

              <td className="small-muted">{recording.sid || recording.id || "—"}</td>

              <td>
                {recording.startedAt?.seconds
                  ? new Date(recording.startedAt.seconds * 1000).toLocaleString()
                  : "—"}
              </td>

              <td>
                {recording.stoppedAt?.seconds
                  ? new Date(recording.stoppedAt.seconds * 1000).toLocaleString()
                  : "—"}
              </td>

              <td className="small-muted">
                {recording.storagePaths?.length
                  ? `${recording.storagePaths.length} file(s)`
                  : "—"}
              </td>

              <td>
                <button
                  className="enable-action-btn"
                  onClick={() => handleOpenRecording(recording)}
                  disabled={recordingLoading || !recording.storagePaths?.length}
                >
                  Open Recording
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )}
</div>

<div style={{ gridColumn: "1 / -1" }}>
  <strong>Recording History</strong>

  {auctionRecordingSessions.length === 0 ? (
    <p>—</p>
  ) : (
    <div className="table-wrapper" style={{ marginTop: 10 }}>
      <table>
        <thead>
          <tr>
            <th>Session</th>
            <th>Status</th>
            <th>Started</th>
            <th>Stopped</th>
            <th>SID</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {auctionRecordingSessions.map((recording, index) => (
            <tr key={recording.id}>
              <td>Recording #{auctionRecordingSessions.length - index}</td>

              <td>
                <span className={`badge ${recording.status || "unknown"}`}>
                  {recording.status || "unknown"}
                </span>
              </td>

              <td>
                {recording.startedAt?.seconds
                  ? new Date(recording.startedAt.seconds * 1000).toLocaleString()
                  : "—"}
              </td>

              <td>
                {recording.stoppedAt?.seconds
                  ? new Date(recording.stoppedAt.seconds * 1000).toLocaleString()
                  : "—"}
              </td>

              <td className="small-muted">{recording.sid || recording.id || "—"}</td>

              <td>
                <button
                  className="enable-action-btn"
                  onClick={() => handleOpenRecording(recording)}
                  disabled={recordingLoading || !recording.storagePaths?.length}
                >
                  {recordingLoading ? "Opening..." : "Open Recording"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )}
</div>

<div>
  <strong>Extension Count</strong>
  <p>{Number(selectedAuctionDetails.extensionCount || 0)}</p>
</div>

<div>
  <strong>Extension Total Amount</strong>
  <p>
    {Number(selectedAuctionDetails.extensionTotalAmountKWD || 0).toFixed(3)} KWD
  </p>
</div>

<div>
  <strong>Last Extension Paid At</strong>
  <p>
    {selectedAuctionDetails.lastExtensionPaidAt
      ? new Date(Number(selectedAuctionDetails.lastExtensionPaidAt)).toLocaleString()
      : "—"}
  </p>
</div>

<div>
  <strong>Last Extension Billing Note</strong>
  <p>{selectedAuctionDetails.lastExtensionBillingNote || "—"}</p>
</div>

<div>
  <strong>Extension Billing History</strong>
  <p>
    {selectedAuctionDetails.extensionBillingNotes?.length
      ? selectedAuctionDetails.extensionBillingNotes
          .map(
            (item, index) =>
              `${index + 1}. ${
                item.note || "No note"
              } — ${Number(item.amountKWD || 0).toFixed(3)} KWD — ${
                item.createdAt
                  ? new Date(Number(item.createdAt)).toLocaleString()
                  : "Unknown date"
              }`
          )
          .join(" | ")
      : "—"}
  </p>
</div>

<div>
  <strong>REVENUE SUMMARY</strong>
  <p>
    Initial Revenue:{" "}
    {Number(selectedAuctionDetails.billingAmountKWD || 0).toFixed(3)} KWD
    {" | "}
    Extension Revenue:{" "}
    {Number(selectedAuctionDetails.extensionTotalAmountKWD || 0).toFixed(3)} KWD
    {" | "}
    Total Auction Revenue:{" "}
    {(
      Number(selectedAuctionDetails.billingAmountKWD || 0) +
      Number(selectedAuctionDetails.extensionTotalAmountKWD || 0)
    ).toFixed(3)} KWD
  </p>
</div>

<div>
  <strong>Billing Type</strong>
  <p>{selectedAuctionDetails.billingType || "—"}</p>
</div>

<div>
  <strong>Billing Amount</strong>
  <p>
    {selectedAuctionDetails.billingAmountKWD !== undefined
      ? `${Number(selectedAuctionDetails.billingAmountKWD || 0).toFixed(3)} KWD`
      : "—"}
  </p>
</div>

<div>
  <strong>Billing Duration</strong>
  <p>
    {selectedAuctionDetails.billingDurationMinutes
      ? `${selectedAuctionDetails.billingDurationMinutes} min`
      : "—"}
  </p>
</div>

<div>
  <strong>Billing Created At</strong>
  <p>
    {selectedAuctionDetails.billingCreatedAt
      ? new Date(
          Number(selectedAuctionDetails.billingCreatedAt)
        ).toLocaleString()
      : "—"}
  </p>
</div>

<div>
  <strong>Payment / Billing Note</strong>
  <p>
    {selectedAuctionDetails.billingNote ||
      selectedAuctionDetails.paymentNote ||
      "—"}
  </p>
</div>

<div>
  <strong>Admin Notes</strong>

  <p>
    {selectedAuctionDetails.adminNotes?.length
      ? selectedAuctionDetails.adminNotes
          .map(
            (item, index) =>
              `${index + 1}. ${item.note || "No note"} — ${
                item.adminEmail || "Unknown admin"
              } — ${
                item.createdAt
                  ? new Date(Number(item.createdAt)).toLocaleString()
                  : "Unknown date"
              }`
          )
          .join(" | ")
      : "—"}
  </p>

  <button
    className="flag-action-btn"
    onClick={handleAddAuctionAdminNote}
    style={{ marginTop: 10 }}
  >
    Add Admin Note
  </button>
</div>
    </div>
  </section>
)}

      <section className={activeSection === "payments" ? "table-card" : "hidden-section"}>
      <div className="table-header">
  <div>
    <h2>Top Buyers</h2>
    <p>Leaderboard of buyers based on auctions won and winning value.</p>
  </div>

  <button
    className="enable-action-btn"
    onClick={handleExportTopBuyersCsv}
  >
    Export Top Buyers CSV
  </button>
</div>

        <div className="controls-row">
          <input
            className="search-input"
            placeholder="Search buyer username or email..."
            value={buyerSearch}
            onChange={(e) => setBuyerSearch(e.target.value)}
          />
        </div>

        {topBuyers.length === 0 ? (
          <p className="empty-text">No buyer activity found.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Buyer</th>
                  <th>Email</th>
                  <th>Auctions Won</th>
                  <th>Total Winning Value</th>
                  <th>Latest Won Auction</th>
                  <th>Last Win Date</th>
                </tr>
              </thead>

              <tbody>
                {topBuyers.map((buyer) => (
                  <tr key={buyer.buyerName}>
                    <td>{buyer.buyerName || "—"}</td>
                    <td>{buyer.email || "—"}</td>
                    <td>{buyer.auctionsWon}</td>
                    <td>{Number(buyer.totalWinningValue || 0).toFixed(3)} KWD</td>
                    <td>{buyer.latestWonAuction || "—"}</td>
                    <td>
                      {buyer.lastWinDate
                        ? new Date(Number(buyer.lastWinDate)).toLocaleString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className={activeSection === "payments" ? "table-card" : "hidden-section"}>
      <div className="table-header">
  <div>
    <h2>Top Free-Grant Users</h2>
    <p>
      Users who received the highest promo/refund
      credit value.
    </p>
  </div>

  <button
    className="enable-action-btn"
    onClick={handleExportTopFreeGrantUsersCsv}
  >
    Export Free Grants CSV
  </button>
</div>

        {topFreeGrantUsers.length === 0 ? (
          <p className="empty-text">
            No free grants found.
          </p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Free Grants Count</th>
                  <th>Free Grants Value</th>
                  <th>Wallet</th>
                  <th>Current Credits</th>
                  <th>Auctions</th>
                  <th>Last Free Grant</th>
                </tr>
              </thead>

              <tbody>
                {topFreeGrantUsers.map((user, index) => (
                  <tr key={`${user.email}-${index}`}>
                    <td>{user.username || "—"}</td>

                    <td>{user.email || "—"}</td>

                    <td>
                      {Number(
                        user.freeGrantCount || 0
                      )}
                    </td>

                    <td>
                      {Number(
                        user.freeGrants || 0
                      ).toFixed(3)}{" "}
                      KWD
                    </td>

                    <td>
                      {Number(
                        user.walletBalance || 0
                      ).toFixed(3)}{" "}
                      KWD
                    </td>

                    <td>
                      15:{" "}
                      {Number(
                        user.credits?.["15"] || 0
                      )}
                      <br />
                      30:{" "}
                      {Number(
                        user.credits?.["30"] || 0
                      )}
                      <br />
                      60:{" "}
                      {Number(
                        user.credits?.["60"] || 0
                      )}
                      <br />
                      ext15:{" "}
                      {Number(
                        user.credits?.ext15 || 0
                      )}
                    </td>

                    <td>{user.auctionsCreated}</td>

                    <td>
                      {user.lastFreeGrant
                        ? new Date(
                            Number(user.lastFreeGrant)
                          ).toLocaleString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className={activeSection === "payments" ? "table-card" : "hidden-section"}>
      <div className="table-header">
  <div>
    <h2>Top Paying Users</h2>
    <p>Users ranked by total successful payment activity.</p>
  </div>

  <button
    className="enable-action-btn"
    onClick={handleExportTopPayingUsersCsv}
  >
    Export Top Paying Users CSV
  </button>
</div>

        {topPayingUsers.length === 0 ? (
          <p className="empty-text">No payment activity found.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Transactions</th>
                  <th>Total Paid</th>
                  <th>Revenue</th>
                  <th>Free Grants</th>
                  <th>Wallet</th>
                  <th>Auctions</th>
                  <th>Last Top-Up</th>
                </tr>
              </thead>

              <tbody>
                {topPayingUsers.map((user, index) => (
                  <tr key={`${user.email}-${index}`}>
                    <td>{user.username || "—"}</td>

                    <td>{user.email || "—"}</td>

                    <td>{user.transactionCount}</td>

                    <td>
                      {Number(user.totalPaid || 0).toFixed(3)} KWD
                    </td>

                    <td>
                      {Number(user.revenueGenerated || 0).toFixed(3)} KWD
                    </td>

                    <td>
                      {Number(user.freeCredits || 0).toFixed(3)} KWD
                    </td>

                    <td>
                      {Number(user.walletBalance || 0).toFixed(3)} KWD
                    </td>

                    <td>{user.auctionsCreated}</td>

                    <td>
                      {user.lastPayment
                        ? new Date(
                            Number(user.lastPayment)
                          ).toLocaleString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className={activeSection === "payments" ? "table-card" : "hidden-section"}>
      <div className="table-header">
  <div>
    <h2>Transactions / Payment History</h2>
    <p>Wallet top-ups, auction creation charges, and +15 min extension charges.</p>
  </div>

  <button
    className="enable-action-btn"
    onClick={handleExportPaymentsCsv}
  >
    Export Payments CSV
  </button>
</div>

        <div className="controls-row">
          <div className="filter-buttons">
            <button className={transactionStatusFilter === "all" ? "filter-btn active" : "filter-btn"} onClick={() => setTransactionStatusFilter("all")}>All</button>
            <button className={transactionStatusFilter === "succeeded" ? "filter-btn active" : "filter-btn"} onClick={() => setTransactionStatusFilter("succeeded")}>Succeeded</button>
            <button className={transactionStatusFilter === "pending" ? "filter-btn active" : "filter-btn"} onClick={() => setTransactionStatusFilter("pending")}>Pending</button>
            <button className={transactionStatusFilter === "failed" ? "filter-btn active" : "filter-btn"} onClick={() => setTransactionStatusFilter("failed")}>Failed</button>
          </div>

          <div className="summary-pill">
            Net value: {totalTransactionValue.toFixed(3)} KWD
          </div>
        </div>

        {loading ? (
          <p className="loading-text">Loading transactions...</p>
        ) : filteredTransactions.length === 0 ? (
          <p className="empty-text">No transactions match this filter.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                <th>Transaction ID</th>
                <th>User</th>
                <th>Type</th>
                <th>Amount (KWD)</th>
                <th>Allocation</th>
                <th>Impact (KWD)</th>
                  <th>Credit</th>
                  <th>Status</th>
                  <th>Provider</th>
                  <th>Reference</th>
                  <th>Note</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
  {filteredTransactions.map((transaction) => (
    <tr key={transaction.id}>

      <td className="small-muted">
        {transaction.id || "—"}
      </td>

      <td>{getUserLabel(transaction.uid)}</td>
<td>{transaction.type || "—"}</td>
<td className={Number(transaction.amountKWD || 0) < 0 ? "amount-negative" : "amount-positive"}>
  {Number(transaction.amountKWD || 0).toFixed(3)}
</td>
<td>{getTransactionAllocation(transaction).label}</td>

<td>
  {Number(getTransactionAllocation(transaction).impact || 0).toFixed(3)}
</td>
<td>{transaction.durationCredit || "—"}</td>
                    <td>
                      <span className={`badge ${transaction.status || "unknown"}`}>
                        {transaction.status || "unknown"}
                      </span>
                    </td>
                    <td>{transaction.provider || "—"}</td>
                    <td className="small-muted">{transaction.providerRef || transaction.invoiceId || "—"}</td>
                    <td className="small-muted">{transaction.note || "—"}</td>
                    <td>
                      {getTransactionTime(transaction)
                        ? new Date(
                            getTransactionTime(transaction)
                          ).toLocaleString()
                        : "—"}
                    </td>
                    <td>
                      <button
                        className="flag-action-btn"
                        onClick={() => handleMarkTransactionManually(transaction.id)}
                      >
                        Mark
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
            </section>

            <section className={activeSection === "logs" ? "table-card" : "hidden-section"}>
            <div className="table-header">
  <div>
    <h2>Admin Logs</h2>
    <p>Search and review owner/admin actions taken from the control panel.</p>
  </div>

  <button
    className="enable-action-btn"
    onClick={handleExportAdminLogsCsv}
  >
    Export Admin Logs CSV
  </button>
</div>

  <div className="controls-row">
    <input
      className="search-input"
      placeholder="Search action, admin email, user, auction, or transaction..."
      value={adminLogSearch}
      onChange={(e) => setAdminLogSearch(e.target.value)}
    />

    <div className="filter-buttons">
      <button
        className={adminLogActionFilter === "all" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("all")}
      >
        All
      </button>

      <button
        className={adminLogActionFilter === "disable_user" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("disable_user")}
      >
        Disable
      </button>

      <button
        className={adminLogActionFilter === "ban_user" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("ban_user")}
      >
        Ban
      </button>

      <button
        className={adminLogActionFilter === "reset_wallet" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("reset_wallet")}
      >
        Reset Wallet
      </button>

      <button
        className={adminLogActionFilter === "close_auction" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("close_auction")}
      >
        Close Auction
      </button>

      <button
        className={adminLogActionFilter === "remove_auction" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("remove_auction")}
      >
        Remove Auction
      </button>
      <button
        className={adminLogActionFilter === "flag_auction" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("flag_auction")}
      >
        Flag Auction
      </button>

      <button
        className={adminLogActionFilter === "unflag_auction" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("unflag_auction")}
      >
        Unflag Auction
      </button>

      <button
        className={adminLogActionFilter === "enable_user" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("enable_user")}
      >
        Enable
      </button>

      <button
        className={adminLogActionFilter === "unban_user" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("unban_user")}
      >
        Unban
      </button>

      <button
        className={adminLogActionFilter === "grant_admin_role" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("grant_admin_role")}
      >
        Grant Admin
      </button>

      <button
        className={adminLogActionFilter === "remove_admin_role" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("remove_admin_role")}
      >
        Remove Admin
      </button>

      <button
        className={adminLogActionFilter === "archive_user" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("archive_user")}
      >
        Archive User
      </button>

      <button
        className={adminLogActionFilter === "unarchive_user" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("unarchive_user")}
      >
        Unarchive User
      </button>

      <button
        className={adminLogActionFilter === "add_promotional_balance" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("add_promotional_balance")}
      >
        Add Balance
      </button>

      <button
        className={adminLogActionFilter === "refund_credit" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("refund_credit")}
      >
        Refund Credit
      </button>

      <button
        className={adminLogActionFilter === "promotional_credit" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("promotional_credit")}
      >
        Promo Credit
      </button>

      <button
        className={adminLogActionFilter === "mark_transaction_manually" ? "filter-btn active" : "filter-btn"}
        onClick={() => setAdminLogActionFilter("mark_transaction_manually")}
      >
        Mark Transaction
      </button>
    </div>
  </div>

  {loading ? (
    <p className="loading-text">Loading admin logs...</p>
  ) : filteredAdminLogs.length === 0 ? (
    <p className="empty-text">No admin logs match this filter.</p>
  ) : (
    <div className="table-wrapper">
      <table>
        <thead>
        <tr>
<th>Log ID</th>
<th>Action</th>
<th>Admin</th>
<th>Target</th>
<th>Target Email</th>
<th>Target ID / UID</th>
<th>Target Type</th>
<th>Details</th>
<th>Date</th>
          </tr>
        </thead>

        <tbody>
          {filteredAdminLogs.map((log) => (
            <tr key={log.id}>
                <td className="small-muted">{log.id || "—"}</td>

<td>
  <span className={getAdminLogBadgeClass(log.action)}>
    {formatAdminActionLabel(log.action)}
  </span>
</td>

<td>{log.adminEmail || "—"}</td>

              <td>
                {log.targetUsername ||
                  log.targetTitle ||
                  log.targetOwner ||
                  log.targetTransactionUser ||
                  log.targetUid ||
                  log.targetId ||
                  "—"}
              </td>

              <td>
  {log.targetEmail ||
    log.targetOwnerEmail ||
    "—"}
</td>

<td className="small-muted">
  {log.targetUid || log.targetId || "—"}
</td>

<td>{log.targetType || "—"}</td>

              <td className="small-muted">
                {formatAdminLogDetails(log.details)}
              </td>

              <td>{log.createdAtText || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )}
</section>
</div>
);
}

export default App;